import { useEffect, useMemo, useState } from 'react';
import { getFieldsBySectionId, updateField } from '../../../api/fieldsApi';
import {
  deleteMedia,
  getMediaBySectionId,
  updateMedia,
  uploadMedia,
} from '../../../api/mediaApi';
import StatusMessage from '../../../components/dashboard/StatusMessage';
import FieldInputRenderer from '../fields/FieldInputRenderer';
import LinkGroupEditor from './LinkGroupEditor';
import groupNavbarFields from './navbarFieldGroups';
import groupFooterColumnFields from './footerColumnFieldGroups';
import groupMarketsPostFields from './marketsPostFieldGroups';
import groupSolutionsOurExpertiseFields from './expertiseCardFieldGroups';
import groupSolutionsOurApproachFields from './approachStepFieldGroups';
import groupSolutionsWhoWeWorkWithFields from './whoWeWorkWithFieldGroups';
import MarketsPostEditor from './MarketsPostEditor';
import ExpertiseCardEditor from './ExpertiseCardEditor';
import ApproachStepEditor from './ApproachStepEditor';
import WhoWeWorkWithClientEditor from './WhoWeWorkWithClientEditor';
import groupWhyChooseFields from './whyChooseFieldGroups';
import groupDeliverableFields from './deliverableFieldGroups';
import groupGlobalCompaniesFields from './globalCompaniesFieldGroups';
import groupMarketSegmentFields from './marketSegmentFieldGroups';
import groupFeaturedCompanyFields from './featuredCompanyFieldGroups';
import groupWealthPageProductFields from './wealthPageProductFieldGroups';
import groupWealthPageSecurityFields from './wealthPageSecurityFieldGroups';
import groupWealthDetailParameterFields from './wealthDetailParameterFieldGroups';
import groupWealthDetailAccountFeatureFields from './wealthDetailAccountFeatureFieldGroups';
import groupInfrastructureOperationalFields from './infrastructureOperationalFieldGroups';
import groupPlatformsDidFields from './platformsDidFieldGroups';
import groupPlatformsEngineeredFields from './platformsEngineeredFieldGroups';
import groupPlatformsMetaTraderFields from './platformsMetaTraderFieldGroups';
import groupVpsHostingWhyClientsFields from './vpsHostingWhyClientsFieldGroups';
import groupVpsHostingPackagesFields from './vpsHostingPackagesFieldGroups';
import groupVpsHostingIncludedFields from './vpsHostingIncludedFieldGroups';
import groupVpsHostingRequestToResponseFields from './vpsHostingRequestToResponseFieldGroups';
import groupVpsHostingFaqFields from './vpsHostingFaqFieldGroups';
import groupInsightsPillarsFields from './insightsPillarsFieldGroups';
import VpsHostingFaqEditor from './VpsHostingFaqEditor';
import { isSolutionDetailPage } from '../../../utils/solutionDetailSlugs';
import { isMarketDetailPage } from '../../../utils/marketDetailSlugs';
import { isWealthDetailPage } from '../../../utils/wealthDetailSlugs';
import { WEALTH_SAVINGS_PAGE_SLUG } from '../../../utils/wealthSavingsPageSlug';
import { INFRASTRUCTURE_PAGE_SLUG } from '../../../utils/infrastructurePageSlug';
import { PLATFORMS_PAGE_SLUG } from '../../../utils/platformsPageSlug';
import { MARKET_ANALYSIS_PAGE_SLUG } from '../../../utils/marketAnalysisPageSlug';
import { isMarketAnalysisPostPage } from '../../../utils/marketAnalysisPostMetadata';
import MarketAnalysisPostFeaturedEditor from './MarketAnalysisPostFeaturedEditor';
import { VPS_HOSTING_PAGE_SLUG } from '../../../utils/vpsHostingPageSlug';
import { INSIGHTS_PAGE_SLUG } from '../../../utils/insightsPageSlug';
import { ECONOMIC_CALENDAR_PAGE_SLUG } from '../../../utils/economicCalendarPageSlug';
import { CONTACT_PAGE_SLUG } from '../../../utils/contactPageSlug';
import groupEconomicCalendarEventImpactFields from './economicCalendarEventImpactFieldGroups';
import groupContactSpeakFields from './contactSpeakFieldGroups';
import MediaItemEditor from '../media/MediaItemEditor';
import FooterSocialEditor from '../media/FooterSocialEditor';
import FeatureCardEditor from '../media/FeatureCardEditor';
import WhatWeDoRowEditor from '../media/WhatWeDoRowEditor';
import GlobalMarketCardEditor from '../media/GlobalMarketCardEditor';
import WealthProductCardEditor from '../media/WealthProductCardEditor';
import { resolveMediaUrl } from '../../../utils/mediaUrl';

const isUrlField = (field) =>
  field.key === 'url' || field.key.endsWith('-url') || field.fieldType === 'url';

const parseMediaCaption = (caption) => {
  if (!caption) {
    return {};
  }

  try {
    return JSON.parse(caption);
  } catch {
    return {};
  }
};

const parseCaption = (caption) => {
  if (!caption) {
    return { title: '', subtitle: '', url: '' };
  }

  try {
    const parsed = JSON.parse(caption);
    return {
      title: parsed.title || '',
      subtitle: parsed.subtitle || '',
      url: parsed.url || '',
    };
  } catch {
    return { title: '', subtitle: '', url: '' };
  }
};

const parseGlobalMarketCaption = (caption) => {
  const parsed = parseMediaCaption(caption);
  return {
    name: parsed.name || '',
    items: parsed.items || '',
    linkUrl: parsed.linkUrl || '',
  };
};

const parseWealthProductCaption = (caption) => {
  const parsed = parseMediaCaption(caption);
  return {
    name: parsed.name || '',
    description: parsed.description || '',
    features: Array.isArray(parsed.features) ? parsed.features : [],
    linkUrl: parsed.linkUrl || '',
  };
};

const parseWhatWeDoRowCaption = (caption) => {
  const parsed = parseMediaCaption(caption);
  return {
    title: parsed.title || '',
    description: parsed.description || '',
    linkUrl: parsed.linkUrl || '',
    reverse: parsed.reverse === true || parsed.reverse === 'true',
  };
};

const SectionBlock = ({ title, description, children }) => (
  <section className="rounded-md border border-[#e4d9c8] bg-white">
    <div className="border-b border-[#e4d9c8] px-5 py-4">
      <h4 className="text-sm font-semibold text-[#130705]">{title}</h4>
      {description ? (
        <p className="mt-1 text-[13px] leading-relaxed text-[#8a8178]">{description}</p>
      ) : null}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const SectionEditor = ({ section, pageSlug, pageDescription, pageId, onSaved }) => {
  const [fields, setFields] = useState([]);
  const [media, setMedia] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [mediaValues, setMediaValues] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [isAddingSocial, setIsAddingSocial] = useState(false);
  const [uploadingRowImageId, setUploadingRowImageId] = useState(null);
  const [uploadingRowIconId, setUploadingRowIconId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const isNavbar = section.key === 'navbar';
  const isMegaMenu = section.key.startsWith('navbar-mega-');
  const isFooter = section.key === 'footer';
  const isFooterColumn = section.key.startsWith('footer-column-');
  const isHomeHero = section.key === 'hero' && pageSlug === 'home';
  const isSolutionsHero = section.key === 'hero' && pageSlug === 'solutions';
  const isWealthPage = pageSlug === WEALTH_SAVINGS_PAGE_SLUG;
  const isInfrastructurePage = pageSlug === INFRASTRUCTURE_PAGE_SLUG;
  const isPlatformsPage = pageSlug === PLATFORMS_PAGE_SLUG;
  const isVpsHostingPage = pageSlug === VPS_HOSTING_PAGE_SLUG;
  const isWealthPageHero = isWealthPage && section.key === 'hero';
  const isSolutionDetail = isSolutionDetailPage(pageSlug);
  const isMarketDetail = isMarketDetailPage(pageSlug);
  const isWealthDetail = isWealthDetailPage(pageSlug);
  const isSolutionDetailHero = isSolutionDetail && section.key === 'hero';
  const isMarketDetailHero = isMarketDetail && section.key === 'hero';
  const isWealthDetailHero = isWealthDetail && section.key === 'hero';
  const isPageHero = isHomeHero || isSolutionsHero || isSolutionDetailHero || isWealthPageHero;
  const isTtg = section.key === 'ttg';
  const isWhatWeDo = section.key === 'what-we-do';
  const isGlobalMarkets = section.key === 'global-markets';
  const isWealthSavings = section.key === 'wealth-savings';
  const isHomeMarkets = section.key === 'markets';
  const isSolutionsOurExpertise = section.key === 'our-expertise';
  const isTailoredStrategies = section.key === 'tailored-strategies';
  const isOurApproach = section.key === 'our-approach';
  const isWhoWeWorkWith = section.key === 'who-we-work-with';
  const isAdvantage = section.key === 'advantage';
  const isLetsBuild = section.key === 'lets-build';
  const isReady = section.key === 'ready';
  const isSolutionDetailView = isSolutionDetail && section.key === 'view';
  const isSolutionDetailWhatWeDeliver = isSolutionDetail && section.key === 'what-we-deliver';
  const isSolutionDetailWhyChoose = isSolutionDetail && section.key === 'why-choose';
  const isMarketDetailGlobalCompanies = isMarketDetail && section.key === 'global-companies';
  const isMarketDetailMarketSegments = isMarketDetail && section.key === 'market-segments';
  const isMarketDetailFeaturedCompanies = isMarketDetail && section.key === 'featured-companies';
  const isMarketDetailMarketAccess = isMarketDetail && section.key === 'market-access';
  const isMarketDetailRisk = isMarketDetail && section.key === 'risk';
  const isMarketDetailStart = isMarketDetail && section.key === 'start';
  const isWealthApproach = isWealthPage && section.key === 'approach';
  const isWealthProducts = isWealthPage && section.key === 'products';
  const isWealthSecurity = isWealthPage && section.key === 'security';
  const isWealthHowToGetStarted = isWealthPage && section.key === 'how-to-get-started';
  const isWealthBuildYourHeritage = isWealthPage && section.key === 'build-your-heritage';
  const isWealthDetailEarn = isWealthDetail && section.key === 'earn';
  const isWealthDetailCoreParameters = isWealthDetail && section.key === 'core-parameters';
  const isWealthDetailSavings = isWealthDetail && section.key === 'savings';
  const isWealthDetailAccountFeatures = isWealthDetail && section.key === 'account-features';
  const isWealthDetailGrowth = isWealthDetail && section.key === 'growth';
  const isInfrastructureHero = isInfrastructurePage && section.key === 'hero';
  const isInfrastructureProtocols = isInfrastructurePage && section.key === 'protocols';
  const isInfrastructureOperational = isInfrastructurePage && section.key === 'operational';
  const isInfrastructureGlobal = isInfrastructurePage && section.key === 'global';
  const isInfrastructureNeverStatic = isInfrastructurePage && section.key === 'never-static';
  const isInfrastructureBankOn = isInfrastructurePage && section.key === 'bank-on';
  const isPlatformsHero = isPlatformsPage && section.key === 'hero';
  const isPlatformsDid = isPlatformsPage && section.key === 'did';
  const isPlatformsEngineered = isPlatformsPage && section.key === 'engineered';
  const isPlatformsMetaTrader = isPlatformsPage && section.key === 'metatrader';
  const isPlatformsEntryPoints = isPlatformsPage && section.key === 'entry-points';
  const isPlatformsAwaits = isPlatformsPage && section.key === 'awaits';
  const isVpsHostingHero = isVpsHostingPage && section.key === 'hero';
  const isVpsHostingDedicated = isVpsHostingPage && section.key === 'dedicated';
  const isVpsHostingWhyClients = isVpsHostingPage && section.key === 'why-clients';
  const isVpsHostingPackages = isVpsHostingPage && section.key === 'packages';
  const isVpsHostingIncluded = isVpsHostingPage && section.key === 'included';
  const isVpsHostingRequestToResponse = isVpsHostingPage && section.key === 'request-to-response';
  const isVpsHostingAccessControl = isVpsHostingPage && section.key === 'access-control';
  const isVpsHostingFaq = isVpsHostingPage && section.key === 'faq';
  const isVpsHostingContinuity = isVpsHostingPage && section.key === 'continuity';
  const isMarketAnalysisPage = pageSlug === MARKET_ANALYSIS_PAGE_SLUG;
  const isMarketAnalysisPost = isMarketAnalysisPostPage(pageDescription);
  const isMarketAnalysisHero = isMarketAnalysisPage && section.key === 'hero';
  const isMarketAnalysisPostsSettings = isMarketAnalysisPage && section.key === 'posts';
  const isMarketAnalysisMarketInsights = isMarketAnalysisPage && section.key === 'market-insights';
  const isMarketAnalysisInsightToStrategy = isMarketAnalysisPage && section.key === 'insight-to-strategy';
  const isMarketAnalysisPostMeta = isMarketAnalysisPost && section.key === 'post';
  const isMarketAnalysisPostContent = isMarketAnalysisPost && section.key === 'content';
  const isInsightsPage = pageSlug === INSIGHTS_PAGE_SLUG;
  const isInsightsHero = isInsightsPage && section.key === 'hero';
  const isInsightsPillars = isInsightsPage && section.key === 'pillars';
  const isInsightsFeatured = isInsightsPage && section.key === 'featured';
  const isInsightsNews = isInsightsPage && section.key === 'news';
  const isInsightsSubscribe = isInsightsPage && section.key === 'subscribe';
  const isInsightsHeritage = isInsightsPage && section.key === 'heritage';
  const isEconomicCalendarPage = pageSlug === ECONOMIC_CALENDAR_PAGE_SLUG;
  const isEconomicCalendarHero = isEconomicCalendarPage && section.key === 'hero';
  const isEconomicCalendarEventImpact = isEconomicCalendarPage && section.key === 'event-impact';
  const isEconomicCalendarSimpleWorkflow = isEconomicCalendarPage && section.key === 'simple-workflow';
  const isEconomicCalendarMacroHighlights = isEconomicCalendarPage && section.key === 'macro-highlights';
  const isEconomicCalendarBehindTheData = isEconomicCalendarPage && section.key === 'behind-the-data';
  const isContactPage = pageSlug === CONTACT_PAGE_SLUG;
  const isContactHero = isContactPage && section.key === 'hero';
  const isContactForm = isContactPage && section.key === 'form';
  const isContactSpeak = isContactPage && section.key === 'speak';
  const isContactPartner = isContactPage && section.key === 'partner';
  const isHowWeWork = section.key === 'how-we-work';
  const isApproachSteps = isOurApproach || isHowWeWork || isWealthHowToGetStarted || isEconomicCalendarSimpleWorkflow;
  const isTimeIsValuable = section.key === 'time-is-valuable';
  const isCtaSection = isLetsBuild || isReady || isTimeIsValuable || isMarketDetailStart || isWealthBuildYourHeritage || isWealthDetailGrowth || isInfrastructureBankOn || isPlatformsAwaits || isVpsHostingContinuity || isMarketAnalysisInsightToStrategy || isInsightsHeritage || isEconomicCalendarBehindTheData || isContactPartner;

  const logoMedia = useMemo(
    () => media.find((item) => item.groupKey === 'logo' || (isNavbar && item.isPrimary)),
    [media, isNavbar]
  );

  const backgroundMedia = useMemo(
    () => media.find((item) => item.groupKey === 'background' || (isPageHero && item.isPrimary)),
    [media, isPageHero]
  );

  const heroImageMedia = useMemo(
    () => media.find((item) => item.groupKey === 'hero-image' || ((isMarketDetailHero || isWealthDetailHero || isInfrastructureHero || isPlatformsHero || isVpsHostingHero || isInsightsHero || isContactHero) && item.isPrimary)),
    [media, isMarketDetailHero, isWealthDetailHero, isInfrastructureHero, isPlatformsHero, isVpsHostingHero, isInsightsHero, isContactHero]
  );

  const logomarkMedia = useMemo(
    () => media.find((item) => item.groupKey === 'logomark'),
    [media]
  );

  const itemMedia = useMemo(
    () => media.filter((item) => item.groupKey === 'items'),
    [media]
  );

  const socialMedia = useMemo(
    () => media.filter((item) => item.groupKey === 'socials').sort((a, b) => a.sortOrder - b.sortOrder),
    [media]
  );

  const eagleMedia = useMemo(
    () => media.find((item) => item.groupKey === 'eagle'),
    [media]
  );

  const riskEagleMedia = useMemo(
    () => media.find((item) => item.groupKey === 'eagle-image'),
    [media]
  );

  const cardMedia = useMemo(
    () => media.filter((item) => item.groupKey === 'cards').sort((a, b) => a.sortOrder - b.sortOrder),
    [media]
  );

  const rowMedia = useMemo(
    () => media.filter((item) => item.groupKey === 'rows').sort((a, b) => a.sortOrder - b.sortOrder),
    [media]
  );

  const marketMedia = useMemo(
    () => media.filter((item) => item.groupKey === 'markets').sort((a, b) => a.sortOrder - b.sortOrder),
    [media]
  );

  const featureImageMedia = useMemo(
    () =>
      media.find(
        (item) =>
          item.groupKey === 'feature-image' ||
          ((isWealthSavings || isTailoredStrategies || isSolutionDetailWhyChoose || isMarketDetailGlobalCompanies || isMarketDetailMarketAccess || isWealthApproach || isWealthDetailEarn || isInfrastructureProtocols || isInfrastructureGlobal || isPlatformsMetaTrader || isVpsHostingDedicated || isVpsHostingAccessControl) && item.isPrimary)
      ),
    [media, isWealthSavings, isTailoredStrategies, isSolutionDetailWhyChoose, isMarketDetailGlobalCompanies, isMarketDetailMarketAccess, isWealthApproach, isWealthDetailEarn, isInfrastructureProtocols, isInfrastructureGlobal, isPlatformsMetaTrader, isVpsHostingDedicated, isVpsHostingAccessControl]
  );

  const checkIconMedia = useMemo(
    () => media.find((item) => item.groupKey === 'check-icon'),
    [media]
  );

  const productMedia = useMemo(
    () => media.filter((item) => item.groupKey === 'products').sort((a, b) => a.sortOrder - b.sortOrder),
    [media]
  );

  const navbarGroups = useMemo(
    () => (isNavbar ? groupNavbarFields(fields) : null),
    [fields, isNavbar]
  );

  const footerColumnGroups = useMemo(
    () => (isFooterColumn ? groupFooterColumnFields(fields) : null),
    [fields, isFooterColumn]
  );

  const marketsPostGroups = useMemo(
    () => (isHomeMarkets ? groupMarketsPostFields(fields) : null),
    [fields, isHomeMarkets]
  );

  const expertiseCardGroups = useMemo(
    () => (isSolutionsOurExpertise ? groupSolutionsOurExpertiseFields(fields) : null),
    [fields, isSolutionsOurExpertise]
  );

  const approachStepGroups = useMemo(
    () => (isApproachSteps ? groupSolutionsOurApproachFields(fields) : null),
    [fields, isApproachSteps]
  );

  const whyChooseGroups = useMemo(
    () => (isSolutionDetailWhyChoose ? groupWhyChooseFields(fields) : null),
    [fields, isSolutionDetailWhyChoose]
  );

  const deliverableGroups = useMemo(
    () => (isSolutionDetailWhatWeDeliver ? groupDeliverableFields(fields) : null),
    [fields, isSolutionDetailWhatWeDeliver]
  );

  const globalCompaniesGroups = useMemo(
    () => (isMarketDetailGlobalCompanies || isWealthApproach || isWealthDetailEarn || isInfrastructureProtocols || isVpsHostingDedicated ? groupGlobalCompaniesFields(fields) : null),
    [fields, isMarketDetailGlobalCompanies, isWealthApproach, isWealthDetailEarn, isInfrastructureProtocols, isVpsHostingDedicated]
  );

  const marketSegmentGroups = useMemo(
    () => (isMarketDetailMarketSegments ? groupMarketSegmentFields(fields) : null),
    [fields, isMarketDetailMarketSegments]
  );

  const featuredCompanyGroups = useMemo(
    () => (isMarketDetailFeaturedCompanies ? groupFeaturedCompanyFields(fields) : null),
    [fields, isMarketDetailFeaturedCompanies]
  );

  const wealthProductGroups = useMemo(
    () => (isWealthProducts ? groupWealthPageProductFields(fields) : null),
    [fields, isWealthProducts]
  );

  const wealthSecurityGroups = useMemo(
    () => (isWealthSecurity ? groupWealthPageSecurityFields(fields) : null),
    [fields, isWealthSecurity]
  );

  const wealthDetailParameterGroups = useMemo(
    () => (isWealthDetailCoreParameters ? groupWealthDetailParameterFields(fields) : null),
    [fields, isWealthDetailCoreParameters]
  );

  const wealthDetailAccountFeatureGroups = useMemo(
    () => (isWealthDetailAccountFeatures ? groupWealthDetailAccountFeatureFields(fields) : null),
    [fields, isWealthDetailAccountFeatures]
  );

  const infrastructureOperationalGroups = useMemo(
    () => (isInfrastructureOperational ? groupInfrastructureOperationalFields(fields) : null),
    [fields, isInfrastructureOperational]
  );

  const solutionDetailViewFields = useMemo(() => {
    if (!isSolutionDetailView) {
      return [];
    }

    const order = ['title', 'text'];
    const headerFields = order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
    const itemFields = fields
      .filter((field) => field.key.startsWith('item-'))
      .sort((a, b) => a.sortOrder - b.sortOrder);

    return [...headerFields, ...itemFields];
  }, [fields, isSolutionDetailView]);

  const solutionDetailHeroFields = useMemo(() => {
    if (!isSolutionDetailHero) {
      return [];
    }

    const order = ['eyebrow', 'title', 'text', 'ctaText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isSolutionDetailHero]);

  const solutionDetailHeroCtaUrlField = useMemo(() => {
    if (!isSolutionDetailHero) {
      return null;
    }

    return fields.find((field) => field.key === 'ctaUrl') || null;
  }, [fields, isSolutionDetailHero]);

  const marketDetailHeroFields = useMemo(() => {
    if (!isMarketDetailHero) {
      return [];
    }

    const order = ['eyebrow', 'title', 'text', 'ctaText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isMarketDetailHero]);

  const marketDetailHeroCtaUrlField = useMemo(() => {
    if (!isMarketDetailHero) {
      return null;
    }

    return fields.find((field) => field.key === 'ctaUrl') || null;
  }, [fields, isMarketDetailHero]);

  const marketDetailAccessFields = useMemo(() => {
    if (!isMarketDetailMarketAccess) {
      return [];
    }

    const order = ['title', 'text', 'ctaText'];
    const headerFields = order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
    const itemFields = fields
      .filter((field) => field.key.startsWith('item-'))
      .sort((a, b) => a.sortOrder - b.sortOrder);

    return [...headerFields, ...itemFields];
  }, [fields, isMarketDetailMarketAccess]);

  const marketDetailAccessCtaUrlField = useMemo(() => {
    if (!isMarketDetailMarketAccess) {
      return null;
    }

    return fields.find((field) => field.key === 'ctaUrl') || null;
  }, [fields, isMarketDetailMarketAccess]);

  const marketDetailRiskFields = useMemo(() => {
    if (!isMarketDetailRisk) {
      return [];
    }

    const order = ['title', 'text'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isMarketDetailRisk]);

  const wealthPageHeroFields = useMemo(() => {
    if (!isWealthPageHero) {
      return [];
    }

    const order = ['eyebrow', 'title', 'text'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isWealthPageHero]);

  const wealthDetailHeroFields = useMemo(() => {
    if (!isWealthDetailHero) {
      return [];
    }

    const order = ['eyebrow', 'title', 'text', 'ctaText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isWealthDetailHero]);

  const wealthDetailHeroCtaUrlField = useMemo(() => {
    if (!isWealthDetailHero) {
      return null;
    }

    return fields.find((field) => field.key === 'ctaUrl') || null;
  }, [fields, isWealthDetailHero]);

  const wealthDetailSavingsFields = useMemo(() => {
    if (!isWealthDetailSavings) {
      return [];
    }

    return fields.filter((field) => field.key === 'text');
  }, [fields, isWealthDetailSavings]);

  const infrastructureHeroFields = useMemo(() => {
    if (!isInfrastructureHero) {
      return [];
    }

    const order = ['eyebrow', 'title', 'text', 'ctaText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isInfrastructureHero]);

  const infrastructureHeroCtaUrlField = useMemo(() => {
    if (!isInfrastructureHero) {
      return null;
    }

    return fields.find((field) => field.key === 'ctaUrl') || null;
  }, [fields, isInfrastructureHero]);

  const infrastructureGlobalFields = useMemo(() => {
    if (!isInfrastructureGlobal) {
      return [];
    }

    const order = ['title', 'text'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isInfrastructureGlobal]);

  const infrastructureNeverStaticFields = useMemo(() => {
    if (!isInfrastructureNeverStatic) {
      return [];
    }

    return fields.filter((field) => field.key === 'text');
  }, [fields, isInfrastructureNeverStatic]);

  const platformsHeroFields = useMemo(() => {
    if (!isPlatformsHero) {
      return [];
    }

    const order = ['eyebrow', 'title', 'text', 'ctaText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isPlatformsHero]);

  const platformsHeroCtaUrlField = useMemo(() => {
    if (!isPlatformsHero) {
      return null;
    }

    return fields.find((field) => field.key === 'ctaUrl') || null;
  }, [fields, isPlatformsHero]);

  const platformsEntryPointsFields = useMemo(() => {
    if (!isPlatformsEntryPoints) {
      return [];
    }

    const order = ['title', 'text'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isPlatformsEntryPoints]);

  const platformsEntryPointsIconMedia = useMemo(
    () => media.find((item) => item.groupKey === 'feature-icon'),
    [media]
  );

  const platformsDidGroups = useMemo(
    () => (isPlatformsDid ? groupPlatformsDidFields(fields) : null),
    [fields, isPlatformsDid]
  );

  const platformsEngineeredGroups = useMemo(
    () => (isPlatformsEngineered ? groupPlatformsEngineeredFields(fields) : null),
    [fields, isPlatformsEngineered]
  );

  const platformsMetaTraderGroups = useMemo(
    () => (isPlatformsMetaTrader ? groupPlatformsMetaTraderFields(fields) : null),
    [fields, isPlatformsMetaTrader]
  );

  const vpsHostingHeroFields = useMemo(() => {
    if (!isVpsHostingHero) {
      return [];
    }

    const order = ['eyebrow', 'title', 'text', 'ctaText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isVpsHostingHero]);

  const vpsHostingHeroCtaUrlField = useMemo(() => {
    if (!isVpsHostingHero) {
      return null;
    }

    return fields.find((field) => field.key === 'ctaUrl') || null;
  }, [fields, isVpsHostingHero]);

  const vpsHostingAccessControlFields = useMemo(() => {
    if (!isVpsHostingAccessControl) {
      return [];
    }

    const order = ['title', 'text', 'note'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isVpsHostingAccessControl]);

  const vpsHostingNoteIconMedia = useMemo(
    () => media.find((item) => item.groupKey === 'note-icon'),
    [media]
  );

  const vpsHostingCheckIconMedia = useMemo(
    () => media.find((item) => item.groupKey === 'check-icon'),
    [media]
  );

  const vpsHostingWhyClientsGroups = useMemo(
    () => (isVpsHostingWhyClients ? groupVpsHostingWhyClientsFields(fields) : null),
    [fields, isVpsHostingWhyClients]
  );

  const vpsHostingPackagesGroups = useMemo(
    () => (isVpsHostingPackages ? groupVpsHostingPackagesFields(fields) : null),
    [fields, isVpsHostingPackages]
  );

  const vpsHostingIncludedGroups = useMemo(
    () => (isVpsHostingIncluded ? groupVpsHostingIncludedFields(fields) : null),
    [fields, isVpsHostingIncluded]
  );

  const vpsHostingRequestToResponseGroups = useMemo(
    () => (isVpsHostingRequestToResponse ? groupVpsHostingRequestToResponseFields(fields) : null),
    [fields, isVpsHostingRequestToResponse]
  );

  const vpsHostingFaqGroups = useMemo(
    () => (isVpsHostingFaq ? groupVpsHostingFaqFields(fields) : null),
    [fields, isVpsHostingFaq]
  );

  const marketAnalysisHeroFields = useMemo(() => {
    if (!isMarketAnalysisHero) {
      return [];
    }

    const order = ['eyebrow', 'title', 'text'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isMarketAnalysisHero]);

  const marketAnalysisPostsSettingsFields = useMemo(() => {
    if (!isMarketAnalysisPostsSettings) {
      return [];
    }

    return fields.filter((field) => field.key === 'categories');
  }, [fields, isMarketAnalysisPostsSettings]);

  const marketAnalysisMarketInsightsFields = useMemo(() => {
    if (!isMarketAnalysisMarketInsights) {
      return [];
    }

    const order = ['title', 'text', 'buttonText', 'privacyText', 'privacyLinkText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isMarketAnalysisMarketInsights]);

  const marketAnalysisMarketInsightsPrivacyUrlField = useMemo(() => {
    if (!isMarketAnalysisMarketInsights) {
      return null;
    }

    return fields.find((field) => field.key === 'privacyLinkUrl') || null;
  }, [fields, isMarketAnalysisMarketInsights]);

  const marketAnalysisPostFields = useMemo(() => {
    if (!isMarketAnalysisPostMeta) {
      return [];
    }

    const order = ['category', 'title', 'excerpt', 'author', 'date'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isMarketAnalysisPostMeta]);

  const marketAnalysisPostFeaturedField = useMemo(() => {
    if (!isMarketAnalysisPostMeta) {
      return null;
    }

    return fields.find((field) => field.key === 'featured') || null;
  }, [fields, isMarketAnalysisPostMeta]);

  const marketAnalysisPostImageMedia = useMemo(
    () => media.find((item) => item.groupKey === 'post-image' || (isMarketAnalysisPostMeta && item.isPrimary)),
    [media, isMarketAnalysisPostMeta]
  );

  const marketAnalysisPostAvatarMedia = useMemo(
    () => media.find((item) => item.groupKey === 'avatar'),
    [media]
  );

  const marketAnalysisPostContentFields = useMemo(() => {
    if (!isMarketAnalysisPostContent) {
      return [];
    }

    const order = ['lead', 'body'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isMarketAnalysisPostContent]);

  const insightsHeroFields = useMemo(() => {
    if (!isInsightsHero) {
      return [];
    }

    const order = ['eyebrow', 'title', 'text'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isInsightsHero]);

  const insightsPillarsGroups = useMemo(
    () => (isInsightsPillars ? groupInsightsPillarsFields(fields) : null),
    [fields, isInsightsPillars]
  );

  const insightsFeaturedHeaderFields = useMemo(() => {
    if (!isInsightsFeatured) {
      return [];
    }

    const order = ['eyebrow', 'title', 'ctaText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isInsightsFeatured]);

  const insightsFeaturedHeaderCtaUrlField = useMemo(() => {
    if (!isInsightsFeatured) {
      return null;
    }

    return fields.find((field) => field.key === 'ctaUrl') || null;
  }, [fields, isInsightsFeatured]);

  const insightsFeaturedCardFields = useMemo(() => {
    if (!isInsightsFeatured) {
      return [];
    }

    const order = ['tag', 'cardTitle', 'cardText', 'cardLinkText', 'cardLinkUrl'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isInsightsFeatured]);

  const insightsNewsFields = useMemo(() => {
    if (!isInsightsNews) {
      return [];
    }

    const order = ['eyebrow', 'title', 'ctaText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isInsightsNews]);

  const insightsNewsCtaUrlField = useMemo(() => {
    if (!isInsightsNews) {
      return null;
    }

    return fields.find((field) => field.key === 'ctaUrl') || null;
  }, [fields, isInsightsNews]);

  const insightsSubscribeFields = useMemo(() => {
    if (!isInsightsSubscribe) {
      return [];
    }

    const order = ['title', 'text', 'buttonText', 'privacyText', 'privacyLinkText', 'privacyLinkUrl'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isInsightsSubscribe]);

  const economicCalendarHeroFields = useMemo(() => {
    if (!isEconomicCalendarHero) {
      return [];
    }

    const order = ['eyebrow', 'title', 'text'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isEconomicCalendarHero]);

  const economicCalendarEventImpactGroups = useMemo(
    () => (isEconomicCalendarEventImpact ? groupEconomicCalendarEventImpactFields(fields) : null),
    [fields, isEconomicCalendarEventImpact]
  );

  const economicCalendarMacroHighlightsFields = useMemo(() => {
    if (!isEconomicCalendarMacroHighlights) {
      return [];
    }

    const order = ['title', 'text', 'buttonText', 'privacyText', 'privacyLinkText', 'privacyLinkUrl'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isEconomicCalendarMacroHighlights]);

  const contactHeroFields = useMemo(() => {
    if (!isContactHero) {
      return [];
    }

    const order = ['eyebrow', 'title', 'text'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isContactHero]);

  const contactFormFields = useMemo(() => {
    if (!isContactForm) {
      return [];
    }

    const order = [
      'eyebrow',
      'title',
      'text',
      'buttonText',
      'privacyText',
      'privacyLinkText',
      'privacyLinkUrl',
      'successEyebrow',
      'successTitle',
      'successText',
    ];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isContactForm]);

  const contactSpeakGroups = useMemo(
    () => (isContactSpeak ? groupContactSpeakFields(fields) : null),
    [fields, isContactSpeak]
  );

  const whoWeWorkWithGroups = useMemo(
    () => (isWhoWeWorkWith ? groupSolutionsWhoWeWorkWithFields(fields) : null),
    [fields, isWhoWeWorkWith]
  );

  const headerFields = useMemo(() => {
    if (!isMegaMenu) {
      return [];
    }

    const order = ['title', 'description'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isMegaMenu]);

  const megaMenuUrlField = useMemo(() => {
    if (!isMegaMenu) {
      return null;
    }

    return fields.find((field) => field.key === 'url') || null;
  }, [fields, isMegaMenu]);

  const heroFields = useMemo(() => {
    if (!isHomeHero) {
      return [];
    }

    const order = ['title', 'text', 'ctaText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isHomeHero]);

  const heroCtaUrlField = useMemo(() => {
    if (!isHomeHero) {
      return null;
    }

    return (
      fields.find((field) => field.key === 'ctaUrl') ||
      fields.find((field) => field.key === 'buttonUrl') ||
      null
    );
  }, [fields, isHomeHero]);

  const solutionsHeroFields = useMemo(() => {
    if (!isSolutionsHero) {
      return [];
    }

    const order = ['eyebrow', 'title', 'text'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isSolutionsHero]);

  const tailoredStrategiesHeaderFields = useMemo(() => {
    if (!isTailoredStrategies) {
      return [];
    }

    const order = ['title', 'text', 'ctaText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isTailoredStrategies]);

  const tailoredStrategiesCtaUrlField = useMemo(() => {
    if (!isTailoredStrategies) {
      return null;
    }

    return fields.find((field) => field.key === 'ctaUrl') || null;
  }, [fields, isTailoredStrategies]);

  const tailoredStrategiesFeatureFields = useMemo(() => {
    if (!isTailoredStrategies) {
      return [];
    }

    return fields
      .filter((field) => field.key.startsWith('feature-'))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [fields, isTailoredStrategies]);

  const advantageFields = useMemo(() => {
    if (!isAdvantage) {
      return [];
    }

    const order = ['title', 'text'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isAdvantage]);

  const wealthHeaderFields = useMemo(() => {
    if (!isWealthSavings) {
      return [];
    }

    const order = ['eyebrow', 'title', 'text', 'ctaText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isWealthSavings]);

  const wealthCtaUrlField = useMemo(() => {
    if (!isWealthSavings) {
      return null;
    }

    return fields.find((field) => field.key === 'ctaUrl') || null;
  }, [fields, isWealthSavings]);

  const marketsCtaUrlField = useMemo(() => {
    if (!isHomeMarkets) {
      return null;
    }

    return fields.find((field) => field.key === 'ctaUrl') || null;
  }, [fields, isHomeMarkets]);

  const marketsIntroFields = useMemo(() => {
    if (!isHomeMarkets) {
      return [];
    }

    const order = ['title', 'text', 'ctaText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isHomeMarkets]);

  const readyCtaUrlField = useMemo(() => {
    if (!isReady) {
      return null;
    }

    return fields.find((field) => field.key === 'ctaUrl') || null;
  }, [fields, isReady]);

  const readyFields = useMemo(() => {
    if (!isReady) {
      return [];
    }

    const order = ['title', 'text', 'ctaText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isReady]);

  const letsBuildCtaUrlField = useMemo(() => {
    if (!isCtaSection) {
      return null;
    }

    return fields.find((field) => field.key === 'ctaUrl') || null;
  }, [fields, isCtaSection]);

  const letsBuildFields = useMemo(() => {
    if (!isCtaSection) {
      return [];
    }

    const order = ['title', 'text', 'ctaText'];
    return order
      .map((key) => fields.find((field) => field.key === key))
      .filter(Boolean);
  }, [fields, isCtaSection]);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setIsLoading(true);
      setError('');
      setMessage('');

      try {
        const [loadedFields, loadedMedia] = await Promise.all([
          getFieldsBySectionId(section.id),
          getMediaBySectionId(section.id),
        ]);

        if (!isMounted) {
          return;
        }

        const nextFieldValues = {};
        loadedFields.forEach((field) => {
          nextFieldValues[field.id] = field.value || '';
        });

        const nextMediaValues = {};
        loadedMedia.forEach((item) => {
          if (item.groupKey === 'socials') {
            const parsed = parseMediaCaption(item.caption);
            nextMediaValues[item.id] = {
              name: parsed.name || '',
              href: parsed.href || '',
            };
            return;
          }

          if (item.groupKey === 'cards') {
            const parsed = parseMediaCaption(item.caption);
            nextMediaValues[item.id] = {
              title: parsed.title || '',
              subtitle: parsed.subtitle || '',
            };
            return;
          }

          if (item.groupKey === 'rows') {
            nextMediaValues[item.id] = parseWhatWeDoRowCaption(item.caption);
            return;
          }

          if (item.groupKey === 'markets') {
            nextMediaValues[item.id] = parseGlobalMarketCaption(item.caption);
            return;
          }

          if (item.groupKey === 'products') {
            const parsed = parseWealthProductCaption(item.caption);
            nextMediaValues[item.id] = {
              name: parsed.name || '',
              description: parsed.description || '',
              linkUrl: parsed.linkUrl || '',
              featuresText: (parsed.features || []).join('\n'),
            };
            return;
          }

          nextMediaValues[item.id] = parseCaption(item.caption);
        });

        setFields(loadedFields);
        setMedia(loadedMedia);
        setFieldValues(nextFieldValues);
        setMediaValues(nextMediaValues);
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to load section.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [section.id]);

  const reloadFields = async () => {
    const loadedFields = await getFieldsBySectionId(section.id);
    const nextFieldValues = {};
    loadedFields.forEach((field) => {
      nextFieldValues[field.id] = field.value || '';
    });
    setFields(loadedFields);
    setFieldValues(nextFieldValues);
  };

  useEffect(() => {
    if (!message) {
      return undefined;
    }

    const timer = setTimeout(() => setMessage(''), 5000);
    return () => clearTimeout(timer);
  }, [message]);

  const updateFieldValue = (fieldId, value) => {
    setFieldValues((current) => ({ ...current, [fieldId]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setMessage('');

    try {
      await Promise.all(
        fields
          .filter((field) => !isUrlField(field))
          .map((field) =>
            updateField(field.id, {
              label: field.label,
              fieldType: field.fieldType,
              value: fieldValues[field.id],
              isRequired: field.isRequired,
              isPublished: field.isPublished,
              sortOrder: field.sortOrder,
            })
          )
      );

      await Promise.all(
        itemMedia.map((item) => {
          const values = mediaValues[item.id];
          const original = parseCaption(item.caption);

          return updateMedia(item.id, {
            mediaType: item.mediaType,
            url: item.url,
            thumbnailUrl: item.thumbnailUrl,
            altText: values?.title || item.altText,
            caption: JSON.stringify({
              title: values?.title || '',
              subtitle: values?.subtitle || '',
              url: original.url,
            }),
            groupKey: item.groupKey,
            sortOrder: item.sortOrder,
            isPrimary: item.isPrimary,
            isPublished: item.isPublished,
          });
        })
      );

      await Promise.all(
        socialMedia.map((item) => {
          const values = mediaValues[item.id] || parseMediaCaption(item.caption);

          return updateMedia(item.id, {
            mediaType: item.mediaType,
            url: item.url,
            thumbnailUrl: item.thumbnailUrl,
            altText: values?.name || item.altText,
            caption: JSON.stringify({
              name: values?.name || '',
              href: values?.href || '',
            }),
            groupKey: item.groupKey,
            sortOrder: item.sortOrder,
            isPrimary: item.isPrimary,
            isPublished: item.isPublished,
          });
        })
      );

      await Promise.all(
        cardMedia.map((item) => {
          const values = mediaValues[item.id] || parseMediaCaption(item.caption);

          return updateMedia(item.id, {
            mediaType: item.mediaType,
            url: item.url,
            thumbnailUrl: item.thumbnailUrl,
            altText: values?.title || item.altText,
            caption: JSON.stringify({
              title: values?.title || '',
              subtitle: values?.subtitle || '',
            }),
            groupKey: item.groupKey,
            sortOrder: item.sortOrder,
            isPrimary: item.isPrimary,
            isPublished: item.isPublished,
          });
        })
      );

      await Promise.all(
        rowMedia.map((item) => {
          const values = mediaValues[item.id] || parseWhatWeDoRowCaption(item.caption);
          const original = parseWhatWeDoRowCaption(item.caption);

          return updateMedia(item.id, {
            mediaType: item.mediaType,
            url: item.url,
            thumbnailUrl: item.thumbnailUrl,
            altText: values?.title || item.altText,
            caption: JSON.stringify({
              title: values?.title || '',
              description: values?.description || '',
              linkUrl: original.linkUrl,
              reverse: Boolean(values?.reverse),
            }),
            groupKey: item.groupKey,
            sortOrder: item.sortOrder,
            isPrimary: item.isPrimary,
            isPublished: item.isPublished,
          });
        })
      );

      await Promise.all(
        marketMedia.map((item) => {
          const values = mediaValues[item.id] || parseGlobalMarketCaption(item.caption);
          const original = parseGlobalMarketCaption(item.caption);

          return updateMedia(item.id, {
            mediaType: item.mediaType,
            url: item.url,
            thumbnailUrl: item.thumbnailUrl,
            altText: values?.name || item.altText,
            caption: JSON.stringify({
              name: values?.name || '',
              items: values?.items || '',
              linkUrl: original.linkUrl,
            }),
            groupKey: item.groupKey,
            sortOrder: item.sortOrder,
            isPrimary: item.isPrimary,
            isPublished: item.isPublished,
          });
        })
      );

      await Promise.all(
        productMedia.map((item) => {
          const values = mediaValues[item.id] || parseWealthProductCaption(item.caption);
          const original = parseWealthProductCaption(item.caption);

          return updateMedia(item.id, {
            mediaType: item.mediaType,
            url: item.url,
            thumbnailUrl: item.thumbnailUrl,
            altText: values?.name || item.altText,
            caption: JSON.stringify({
              name: values?.name || '',
              description: values?.description || '',
              linkUrl: original.linkUrl,
              features: (values?.featuresText || '')
                .split('\n')
                .map((feature) => feature.trim())
                .filter(Boolean),
            }),
            groupKey: item.groupKey,
            sortOrder: item.sortOrder,
            isPrimary: item.isPrimary,
            isPublished: item.isPublished,
          });
        })
      );

      setMessage(`${section.name} has been updated successfully.`);
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (event, options = {}) => {
    const {
      groupKey = 'logo',
      isPrimary = true,
      altText = 'VBS',
      successMessage = 'Logo has been updated successfully.',
      mediaType = 'image',
      sortOrder,
    } = options;
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setIsSaving(true);
    setError('');
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', mediaType);
      formData.append('groupKey', groupKey);
      formData.append('altText', altText);
      formData.append('isPrimary', String(isPrimary));
      formData.append('isPublished', 'true');
      formData.append(
        'sortOrder',
        sortOrder ?? (groupKey === 'logomark' ? '2' : groupKey === 'check-icon' ? '2' : '1')
      );

      const uploaded = await uploadMedia(section.id, formData);
      const existingMedia =
        groupKey === 'logomark'
          ? logomarkMedia
          : groupKey === 'background'
            ? backgroundMedia
            : groupKey === 'hero-image'
              ? heroImageMedia
              : groupKey === 'eagle'
                ? eagleMedia
                : groupKey === 'feature-image'
                  ? featureImageMedia
                  : groupKey === 'check-icon'
                    ? checkIconMedia
                    : groupKey.startsWith('item-') && groupKey.endsWith('-icon')
                      ? media.find((item) => item.groupKey === groupKey)
                      : logoMedia;

      if (existingMedia && existingMedia.id !== uploaded.id) {
        await deleteMedia(existingMedia.id);
      }

      setMedia((current) => {
        const withoutOld = current.filter((item) => item.id !== existingMedia?.id);
        return [uploaded, ...withoutOld.filter((item) => item.id !== uploaded.id)];
      });

      setMessage(successMessage);
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Failed to upload logo.');
    } finally {
      setIsSaving(false);
      event.target.value = '';
    }
  };

  const handleItemIconUpload = async (item, event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploadingItemId(item.id);
    setError('');
    setMessage('');

    try {
      const values = mediaValues[item.id] || parseCaption(item.caption);
      const original = parseCaption(item.caption);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', 'icon');
      formData.append('groupKey', item.groupKey || 'items');
      formData.append('altText', values.title || item.altText || 'Menu item');
      formData.append(
        'caption',
        JSON.stringify({
          title: values.title || '',
          subtitle: values.subtitle || '',
          url: original.url,
        })
      );
      formData.append('isPublished', 'true');
      formData.append('sortOrder', String(item.sortOrder));

      const uploaded = await uploadMedia(section.id, formData);
      await deleteMedia(item.id);

      setMedia((current) =>
        [...current.filter((mediaItem) => mediaItem.id !== item.id), uploaded].sort(
          (a, b) => a.sortOrder - b.sortOrder
        )
      );

      setMediaValues((current) => {
        const next = { ...current };
        delete next[item.id];
        next[uploaded.id] = values;
        return next;
      });

      setMessage(`Icon for "${values.title || 'menu item'}" has been updated successfully.`);
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Failed to upload icon.');
    } finally {
      setUploadingItemId(null);
      event.target.value = '';
    }
  };

  const handleSocialIconUpload = async (item, event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploadingItemId(item.id);
    setError('');
    setMessage('');

    try {
      const values = mediaValues[item.id] || parseMediaCaption(item.caption);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', 'icon');
      formData.append('groupKey', 'socials');
      formData.append('altText', values.name || item.altText || 'Social link');
      formData.append(
        'caption',
        JSON.stringify({
          name: values.name || '',
          href: values.href || '',
        })
      );
      formData.append('isPublished', 'true');
      formData.append('sortOrder', String(item.sortOrder));

      const uploaded = await uploadMedia(section.id, formData);
      await deleteMedia(item.id);

      setMedia((current) =>
        [...current.filter((mediaItem) => mediaItem.id !== item.id), uploaded].sort(
          (a, b) => a.sortOrder - b.sortOrder
        )
      );

      setMediaValues((current) => {
        const next = { ...current };
        delete next[item.id];
        next[uploaded.id] = values;
        return next;
      });

      setMessage(`Icon for "${values.name || 'social link'}" has been updated successfully.`);
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Failed to upload icon.');
    } finally {
      setUploadingItemId(null);
      event.target.value = '';
    }
  };

  const handleSocialDelete = async (item) => {
    const values = mediaValues[item.id] || parseMediaCaption(item.caption);
    const label = values.name || item.altText || 'this social link';

    if (!window.confirm(`Remove "${label}" from the footer?`)) {
      return;
    }

    setDeletingItemId(item.id);
    setError('');
    setMessage('');

    try {
      await deleteMedia(item.id);

      setMedia((current) => current.filter((mediaItem) => mediaItem.id !== item.id));
      setMediaValues((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });

      setMessage('Social link has been removed.');
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Failed to remove social link.');
    } finally {
      setDeletingItemId(null);
    }
  };

  const handleAddSocial = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setIsAddingSocial(true);
    setError('');
    setMessage('');

    try {
      const nextSortOrder =
        socialMedia.reduce((max, item) => Math.max(max, item.sortOrder), 0) + 1;
      const defaultValues = { name: 'New social', href: 'https://' };
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', 'icon');
      formData.append('groupKey', 'socials');
      formData.append('altText', defaultValues.name);
      formData.append('caption', JSON.stringify(defaultValues));
      formData.append('isPublished', 'true');
      formData.append('sortOrder', String(nextSortOrder));

      const uploaded = await uploadMedia(section.id, formData);

      setMedia((current) =>
        [...current, uploaded].sort((a, b) => a.sortOrder - b.sortOrder)
      );
      setMediaValues((current) => ({
        ...current,
        [uploaded.id]: defaultValues,
      }));

      setMessage('Social link added. Update the name and URL, then save changes.');
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Failed to add social link.');
    } finally {
      setIsAddingSocial(false);
      event.target.value = '';
    }
  };

  const handleCardIconUpload = async (item, event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploadingItemId(item.id);
    setError('');
    setMessage('');

    try {
      const values = mediaValues[item.id] || parseMediaCaption(item.caption);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', 'icon');
      formData.append('groupKey', 'cards');
      formData.append('altText', values.title || item.altText || 'Feature card');
      formData.append(
        'caption',
        JSON.stringify({
          title: values.title || '',
          subtitle: values.subtitle || '',
        })
      );
      formData.append('isPublished', 'true');
      formData.append('sortOrder', String(item.sortOrder));

      const uploaded = await uploadMedia(section.id, formData);
      await deleteMedia(item.id);

      setMedia((current) =>
        [...current.filter((mediaItem) => mediaItem.id !== item.id), uploaded].sort(
          (a, b) => a.sortOrder - b.sortOrder
        )
      );

      setMediaValues((current) => {
        const next = { ...current };
        delete next[item.id];
        next[uploaded.id] = values;
        return next;
      });

      setMessage(`Icon for "${values.title || 'card'}" has been updated successfully.`);
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Failed to upload icon.');
    } finally {
      setUploadingItemId(null);
      event.target.value = '';
    }
  };

  const handleRowImageUpload = async (item, event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploadingRowImageId(item.id);
    setError('');
    setMessage('');

    try {
      const values = mediaValues[item.id] || parseWhatWeDoRowCaption(item.caption);
      const original = parseWhatWeDoRowCaption(item.caption);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', 'image');
      formData.append('groupKey', 'rows');
      formData.append('altText', values.title || item.altText || 'Service row');
      formData.append(
        'caption',
        JSON.stringify({
          title: values.title || '',
          description: values.description || '',
          linkUrl: original.linkUrl,
          reverse: Boolean(values.reverse),
        })
      );
      if (item.thumbnailUrl) {
        formData.append('thumbnailUrl', item.thumbnailUrl);
      }
      formData.append('isPublished', 'true');
      formData.append('sortOrder', String(item.sortOrder));

      const uploaded = await uploadMedia(section.id, formData);
      await deleteMedia(item.id);

      setMedia((current) =>
        [...current.filter((mediaItem) => mediaItem.id !== item.id), uploaded].sort(
          (a, b) => a.sortOrder - b.sortOrder
        )
      );

      setMediaValues((current) => {
        const next = { ...current };
        delete next[item.id];
        next[uploaded.id] = values;
        return next;
      });

      setMessage(`Image for "${values.title || 'row'}" has been updated successfully.`);
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Failed to upload image.');
    } finally {
      setUploadingRowImageId(null);
      event.target.value = '';
    }
  };

  const handleRowIconUpload = async (item, event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploadingRowIconId(item.id);
    setError('');
    setMessage('');

    try {
      const values = mediaValues[item.id] || parseWhatWeDoRowCaption(item.caption);
      const original = parseWhatWeDoRowCaption(item.caption);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', 'icon');
      formData.append('groupKey', 'rows');
      formData.append('altText', values.title || item.altText || 'Service row icon');
      formData.append('isPublished', 'true');
      formData.append('sortOrder', String(item.sortOrder));

      const uploaded = await uploadMedia(section.id, formData);

      await updateMedia(item.id, {
        mediaType: item.mediaType,
        url: item.url,
        thumbnailUrl: uploaded.url,
        altText: values.title || item.altText,
        caption: JSON.stringify({
          title: values.title || '',
          description: values.description || '',
          linkUrl: original.linkUrl,
          reverse: Boolean(values.reverse),
        }),
        groupKey: item.groupKey,
        sortOrder: item.sortOrder,
        isPrimary: item.isPrimary,
        isPublished: item.isPublished,
      });

      await deleteMedia(uploaded.id);

      setMedia((current) =>
        current.map((mediaItem) =>
          mediaItem.id === item.id ? { ...mediaItem, thumbnailUrl: uploaded.url } : mediaItem
        )
      );

      setMessage(`Icon for "${values.title || 'row'}" has been updated successfully.`);
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Failed to upload icon.');
    } finally {
      setUploadingRowIconId(null);
      event.target.value = '';
    }
  };

  const handleMarketIconUpload = async (item, event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploadingItemId(item.id);
    setError('');
    setMessage('');

    try {
      const values = mediaValues[item.id] || parseGlobalMarketCaption(item.caption);
      const original = parseGlobalMarketCaption(item.caption);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', 'icon');
      formData.append('groupKey', 'markets');
      formData.append('altText', values.name || item.altText || 'Market card');
      formData.append(
        'caption',
        JSON.stringify({
          name: values.name || '',
          items: values.items || '',
          linkUrl: original.linkUrl,
        })
      );
      formData.append('isPublished', 'true');
      formData.append('sortOrder', String(item.sortOrder));

      const uploaded = await uploadMedia(section.id, formData);
      await deleteMedia(item.id);

      setMedia((current) =>
        [...current.filter((mediaItem) => mediaItem.id !== item.id), uploaded].sort(
          (a, b) => a.sortOrder - b.sortOrder
        )
      );

      setMediaValues((current) => {
        const next = { ...current };
        delete next[item.id];
        next[uploaded.id] = values;
        return next;
      });

      setMessage(`Icon for "${values.name || 'market card'}" has been updated successfully.`);
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Failed to upload icon.');
    } finally {
      setUploadingItemId(null);
      event.target.value = '';
    }
  };

  const handleProductIconUpload = async (item, event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploadingItemId(item.id);
    setError('');
    setMessage('');

    try {
      const values = mediaValues[item.id] || parseWealthProductCaption(item.caption);
      const original = parseWealthProductCaption(item.caption);
      const features = (values.featuresText || '')
        .split('\n')
        .map((feature) => feature.trim())
        .filter(Boolean);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', 'icon');
      formData.append('groupKey', 'products');
      formData.append('altText', values.name || item.altText || 'Product card');
      formData.append(
        'caption',
        JSON.stringify({
          name: values.name || '',
          description: values.description || '',
          linkUrl: original.linkUrl,
          features: features.length > 0 ? features : original.features,
        })
      );
      formData.append('isPublished', 'true');
      formData.append('sortOrder', String(item.sortOrder));

      const uploaded = await uploadMedia(section.id, formData);
      await deleteMedia(item.id);

      setMedia((current) =>
        [...current.filter((mediaItem) => mediaItem.id !== item.id), uploaded].sort(
          (a, b) => a.sortOrder - b.sortOrder
        )
      );

      setMediaValues((current) => {
        const next = { ...current };
        delete next[item.id];
        next[uploaded.id] = values;
        return next;
      });

      setMessage(`Icon for "${values.name || 'product'}" has been updated successfully.`);
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Failed to upload icon.');
    } finally {
      setUploadingItemId(null);
      event.target.value = '';
    }
  };

  const saveHint = isHomeHero
    ? 'Save to apply changes to the public home page hero.'
    : isSolutionsHero
      ? 'Save to apply changes to the public solutions page hero.'
      : isTtg
      ? 'Save to apply changes to the Tailored · Trusted · Global section.'
      : isWhatWeDo
        ? 'Save to apply changes to the What We Do section.'
        : isGlobalMarkets
          ? 'Save to apply changes to the Global Markets section.'
          : isWealthSavings
            ? 'Save to apply changes to the Wealth & Savings section.'
            : isHomeMarkets
              ? 'Save to apply changes to the Markets section.'
              : isSolutionsOurExpertise
                ? 'Save to apply changes to the Our Expertise section.'
                : isTailoredStrategies
                  ? 'Save to apply changes to the Tailored Strategies section.'
                  : isApproachSteps
                    ? 'Save to apply changes to this section.'
                    : isWhoWeWorkWith
                      ? 'Save to apply changes to the Who We Work With section.'
                      : isAdvantage
                        ? 'Save to apply changes to The VBS Advantage section.'
                        : isCtaSection
                          ? 'Save to apply changes to this call-to-action section.'
                          : isSolutionDetail
                            ? 'Save to apply changes to this solution detail page.'
                            : isMarketDetail
                              ? 'Save to apply changes to this market detail page.'
                              : isWealthDetail
                                ? 'Save to apply changes to this wealth product page.'
                                : isInfrastructurePage
                                  ? 'Save to apply changes to this infrastructure page.'
                                  : isPlatformsPage
                                    ? 'Save to apply changes to this platforms page.'
                                    : isVpsHostingPage
                                      ? 'Save to apply changes to this VPS hosting page.'
                                      : isMarketAnalysisPage || isMarketAnalysisPost
                                        ? 'Save to apply changes to this market analysis page.'
                                        : isInsightsPage
                                          ? 'Save to apply changes to this insights page.'
                                          : isEconomicCalendarPage
                                            ? 'Save to apply changes to this economic calendar page.'
                                            : isContactPage
                                              ? 'Save to apply changes to this contact page.'
                                              : isReady
                ? 'Save to apply changes to the Ready section.'
                : isFooter || isFooterColumn
      ? 'Save to apply changes to the public footer.'
      : 'Save to apply changes to the public navbar.';

  if (isLoading) {
    return <p className="text-sm text-[#6b635c]">Loading section…</p>;
  }

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <div>
          <h3 className="font-[Merriweather] text-xl text-[#130705]">{section.name}</h3>
          <p className="mt-1 text-sm text-[#8a8178]">{section.key}</p>
        </div>
      </div>

      {isHomeHero && (
        <>
          <SectionBlock
            title="Background image"
            description="Full-width background image behind the hero headline."
          >
            <div className="flex flex-wrap items-center gap-4">
              {backgroundMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(backgroundMedia.url)}
                    alt={backgroundMedia.altText || 'Hero background'}
                    className="h-32 w-auto max-w-full object-cover sm:h-40"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload background image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'background',
                      altText: 'Home hero background',
                      successMessage: 'Hero background image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Hero content"
            description="Headline, supporting copy, and call-to-action button text."
          >
            {heroCtaUrlField && fieldValues[heroCtaUrlField.id] ? (
              <p className="mb-4 truncate text-[11px] text-[#a8a098]">
                CTA route: {fieldValues[heroCtaUrlField.id]}
              </p>
            ) : null}
            <div className="grid gap-4">
              {heroFields.map((field) => (
                <div
                  key={field.id}
                  className={field.fieldType === 'textarea' ? '' : 'sm:max-w-xl'}
                >
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isSolutionsHero && (
        <>
          <SectionBlock
            title="Background image"
            description="Full-width background image behind the hero headline."
          >
            <div className="flex flex-wrap items-center gap-4">
              {backgroundMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(backgroundMedia.url)}
                    alt={backgroundMedia.altText || 'Hero background'}
                    className="h-32 w-auto max-w-full object-cover sm:h-40"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload background image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'background',
                      altText: 'Solutions hero background',
                      successMessage: 'Hero background image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Hero content"
            description="Eyebrow label, headline, and supporting copy. Use a new line in the title for a line break."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {solutionsHeroFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isInfrastructureHero && (
        <>
          <SectionBlock
            title="Hero image"
            description="Image shown on the right side of the infrastructure page."
          >
            <div className="flex flex-wrap items-center gap-4">
              {heroImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(heroImageMedia.url)}
                    alt={heroImageMedia.altText || 'Infrastructure hero image'}
                    className="h-32 w-auto max-w-full object-cover sm:h-40"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload hero image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'hero-image',
                      altText: 'Infrastructure hero image',
                      successMessage: 'Hero image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Hero content"
            description="Eyebrow label, headline, supporting copy, and call-to-action."
          >
            {infrastructureHeroCtaUrlField && fieldValues[infrastructureHeroCtaUrlField.id] ? (
              <p className="mb-4 truncate text-[11px] text-[#a8a098]">
                CTA route: {fieldValues[infrastructureHeroCtaUrlField.id]}
              </p>
            ) : null}
            <div className="grid gap-4 sm:max-w-2xl">
              {infrastructureHeroFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isInfrastructureProtocols && (
        <>
          <SectionBlock
            title="Section content"
            description="Headline and supporting paragraphs."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {globalCompaniesGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
              {globalCompaniesGroups?.paragraphFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Feature image"
            description="Image shown on the left side of the protocols section."
          >
            <div className="flex flex-wrap items-center gap-4">
              {featureImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(featureImageMedia.url)}
                    alt={featureImageMedia.altText || 'Protocols feature image'}
                    className="h-40 w-auto max-w-full object-cover sm:h-48"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload feature image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-image',
                      altText: 'Protocols feature image',
                      successMessage: 'Feature image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isInfrastructureOperational && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title shown above the operational pillars grid."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {infrastructureOperationalGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Operational pillars"
            description="Cards shown in the operational pillars grid."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {infrastructureOperationalGroups?.itemGroups.map((group) => {
                const iconMedia = media.find(
                  (item) => item.groupKey === `item-${group.index}-icon`
                );

                return (
                  <div
                    key={group.index}
                    className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                  >
                    <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                      Pillar {group.index}
                    </p>
                    {iconMedia ? (
                      <img
                        src={resolveMediaUrl(iconMedia.url)}
                        alt={iconMedia.altText || `Pillar ${group.index} icon`}
                        className="mb-3 h-8 w-8"
                      />
                    ) : null}
                    <label className="mb-4 inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-[12px] font-medium text-[#130705] hover:bg-[#faf8f4]">
                      Upload icon
                      <input
                        type="file"
                        accept="image/*,.svg"
                        className="hidden"
                        onChange={(event) =>
                          handleLogoUpload(event, {
                            groupKey: `item-${group.index}-icon`,
                            altText: `Pillar ${group.index} icon`,
                            successMessage: `Pillar ${group.index} icon has been updated successfully.`,
                          })
                        }
                        disabled={isSaving}
                      />
                    </label>
                    <div className="grid gap-3">
                      {group.fields.map((field) => (
                        <div key={field.id}>
                          <label
                            htmlFor={`field-${field.id}`}
                            className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                          >
                            {field.label}
                          </label>
                          <FieldInputRenderer
                            field={field}
                            value={fieldValues[field.id] || ''}
                            onChange={(value) => updateFieldValue(field.id, value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionBlock>
        </>
      )}

      {isInfrastructureGlobal && (
        <>
          <SectionBlock
            title="Section content"
            description="Headline and supporting copy."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {infrastructureGlobalFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="World map"
            description="Map image shown on the right side of the section."
          >
            <div className="flex flex-wrap items-center gap-4">
              {featureImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(featureImageMedia.url)}
                    alt={featureImageMedia.altText || 'Global network map'}
                    className="h-40 w-auto max-w-full object-cover sm:h-48"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload map image
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-image',
                      altText: 'Global network map',
                      successMessage: 'Map image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isInfrastructureNeverStatic && (
        <SectionBlock
          title="Quote"
          description="Quote shown in the never static section."
        >
          <div className="grid gap-4 sm:max-w-2xl">
            {infrastructureNeverStaticFields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
          </div>
        </SectionBlock>
      )}

      {isPlatformsHero && (
        <>
          <SectionBlock
            title="Hero image"
            description="Image shown on the right side of the platforms page."
          >
            <div className="flex flex-wrap items-center gap-4">
              {heroImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(heroImageMedia.url)}
                    alt={heroImageMedia.altText || 'Platforms hero image'}
                    className="h-32 w-auto max-w-full object-cover sm:h-40"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload hero image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'hero-image',
                      altText: 'Platforms hero image',
                      successMessage: 'Hero image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Hero content"
            description="Eyebrow label, headline, supporting copy, and call-to-action."
          >
            {platformsHeroCtaUrlField && fieldValues[platformsHeroCtaUrlField.id] ? (
              <p className="mb-4 truncate text-[11px] text-[#a8a098]">
                CTA route: {fieldValues[platformsHeroCtaUrlField.id]}
              </p>
            ) : null}
            <div className="grid gap-4 sm:max-w-2xl">
              {platformsHeroFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isPlatformsDid && (
        <SectionBlock
          title="Platform access cards"
          description="Cards shown in the desktop, web, and mobile access section."
        >
          <div className="grid gap-4 xl:grid-cols-3">
            {platformsDidGroups?.itemGroups.map((group) => {
              const iconMedia = media.find(
                (item) => item.groupKey === `item-${group.index}-icon`
              );

              return (
                <div
                  key={group.index}
                  className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                >
                  <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                    Card {group.index}
                  </p>
                  {iconMedia ? (
                    <img
                      src={resolveMediaUrl(iconMedia.url)}
                      alt={iconMedia.altText || `Card ${group.index} icon`}
                      className="mb-3 h-8 w-8"
                    />
                  ) : null}
                  <label className="mb-4 inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-[12px] font-medium text-[#130705] hover:bg-[#faf8f4]">
                    Upload icon
                    <input
                      type="file"
                      accept="image/*,.svg"
                      className="hidden"
                      onChange={(event) =>
                        handleLogoUpload(event, {
                          groupKey: `item-${group.index}-icon`,
                          altText: `Card ${group.index} icon`,
                          successMessage: `Card ${group.index} icon has been updated successfully.`,
                        })
                      }
                      disabled={isSaving}
                    />
                  </label>
                  <div className="grid gap-3">
                    {group.fields.map((field) => (
                      <div key={field.id}>
                        <label
                          htmlFor={`field-${field.id}`}
                          className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                        >
                          {field.label}
                        </label>
                        <FieldInputRenderer
                          field={field}
                          value={fieldValues[field.id] || ''}
                          onChange={(value) => updateFieldValue(field.id, value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </SectionBlock>
      )}

      {isPlatformsEngineered && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title shown above the engineered features grid."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {platformsEngineeredGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Feature cards"
            description="Cards shown in the engineered for precision grid."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {platformsEngineeredGroups?.featureGroups.map((group) => (
                <div
                  key={group.index}
                  className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                >
                  <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                    Feature {group.index}
                  </p>
                  <div className="grid gap-3">
                    {group.fields.map((field) => (
                      <div key={field.id}>
                        <label
                          htmlFor={`field-${field.id}`}
                          className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                        >
                          {field.label}
                        </label>
                        <FieldInputRenderer
                          field={field}
                          value={fieldValues[field.id] || ''}
                          onChange={(value) => updateFieldValue(field.id, value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isPlatformsMetaTrader && (
        <>
          <SectionBlock
            title="Section content"
            description="Headline and supporting copy for the MetaTrader section."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {platformsMetaTraderGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Platform image"
            description="Image shown on the right side of the MetaTrader section."
          >
            <div className="flex flex-wrap items-center gap-4">
              {featureImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(featureImageMedia.url)}
                    alt={featureImageMedia.altText || 'MetaTrader platform image'}
                    className="h-40 w-auto max-w-full object-cover sm:h-48"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload platform image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-image',
                      altText: 'MetaTrader platform image',
                      successMessage: 'Platform image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Download links"
            description="App store and platform download buttons."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {platformsMetaTraderGroups?.downloadGroups.map((group) => {
                const iconMedia = media.find(
                  (item) => item.groupKey === `download-${group.index}-icon`
                );

                return (
                  <div
                    key={group.index}
                    className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                  >
                    <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                      Download {group.index}
                    </p>
                    {iconMedia ? (
                      <img
                        src={resolveMediaUrl(iconMedia.url)}
                        alt={iconMedia.altText || `Download ${group.index} icon`}
                        className="mb-3 h-8 w-8"
                      />
                    ) : null}
                    <label className="mb-4 inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-[12px] font-medium text-[#130705] hover:bg-[#faf8f4]">
                      Upload icon
                      <input
                        type="file"
                        accept="image/*,.svg"
                        className="hidden"
                        onChange={(event) =>
                          handleLogoUpload(event, {
                            groupKey: `download-${group.index}-icon`,
                            altText: `Download ${group.index} icon`,
                            successMessage: `Download ${group.index} icon has been updated successfully.`,
                          })
                        }
                        disabled={isSaving}
                      />
                    </label>
                    <div className="grid gap-3">
                      {group.fields.map((field) => (
                        <div key={field.id}>
                          <label
                            htmlFor={`field-${field.id}`}
                            className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                          >
                            {field.label}
                          </label>
                          <FieldInputRenderer
                            field={field}
                            value={fieldValues[field.id] || ''}
                            onChange={(value) => updateFieldValue(field.id, value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionBlock>
        </>
      )}

      {isPlatformsEntryPoints && (
        <>
          <SectionBlock
            title="Section content"
            description="Headline and supporting copy for the secure entry points section."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {platformsEntryPointsFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Section icon"
            description="Icon shown above the secure entry points headline."
          >
            <div className="flex flex-wrap items-center gap-4">
              {platformsEntryPointsIconMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(platformsEntryPointsIconMedia.url)}
                    alt={platformsEntryPointsIconMedia.altText || 'Entry points icon'}
                    className="h-16 w-16"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload icon
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-icon',
                      altText: 'Entry points icon',
                      successMessage: 'Entry points icon has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isVpsHostingHero && (
        <>
          <SectionBlock
            title="Hero image"
            description="Image shown on the right side of the VPS hosting page."
          >
            <div className="flex flex-wrap items-center gap-4">
              {heroImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(heroImageMedia.url)}
                    alt={heroImageMedia.altText || 'VPS hosting hero image'}
                    className="h-32 w-auto max-w-full object-cover sm:h-40"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload hero image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'hero-image',
                      altText: 'VPS hosting hero image',
                      successMessage: 'Hero image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Hero content"
            description="Eyebrow label, headline, supporting copy, and call-to-action."
          >
            {vpsHostingHeroCtaUrlField && fieldValues[vpsHostingHeroCtaUrlField.id] ? (
              <p className="mb-4 truncate text-[11px] text-[#a8a098]">
                CTA route: {fieldValues[vpsHostingHeroCtaUrlField.id]}
              </p>
            ) : null}
            <div className="grid gap-4 sm:max-w-2xl">
              {vpsHostingHeroFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isVpsHostingDedicated && (
        <>
          <SectionBlock
            title="Section content"
            description="Headline and supporting paragraphs."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {globalCompaniesGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
              {globalCompaniesGroups?.paragraphFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Feature image"
            description="Image shown on the left side of the dedicated section."
          >
            <div className="flex flex-wrap items-center gap-4">
              {featureImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(featureImageMedia.url)}
                    alt={featureImageMedia.altText || 'Dedicated environment image'}
                    className="h-40 w-auto max-w-full object-cover sm:h-48"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload feature image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-image',
                      altText: 'Dedicated environment image',
                      successMessage: 'Feature image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isVpsHostingWhyClients && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title shown above the why clients grid."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {vpsHostingWhyClientsGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Reason cards"
            description="Cards shown in the why clients choose VPS grid."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {vpsHostingWhyClientsGroups?.itemGroups.map((group) => {
                const iconMedia = media.find(
                  (item) => item.groupKey === `item-${group.index}-icon`
                );

                return (
                  <div
                    key={group.index}
                    className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                  >
                    <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                      Card {group.index}
                    </p>
                    {iconMedia ? (
                      <img
                        src={resolveMediaUrl(iconMedia.url)}
                        alt={iconMedia.altText || `Card ${group.index} icon`}
                        className="mb-3 h-8 w-8"
                      />
                    ) : null}
                    <label className="mb-4 inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-[12px] font-medium text-[#130705] hover:bg-[#faf8f4]">
                      Upload icon
                      <input
                        type="file"
                        accept="image/*,.svg"
                        className="hidden"
                        onChange={(event) =>
                          handleLogoUpload(event, {
                            groupKey: `item-${group.index}-icon`,
                            altText: `Card ${group.index} icon`,
                            successMessage: `Card ${group.index} icon has been updated successfully.`,
                          })
                        }
                        disabled={isSaving}
                      />
                    </label>
                    <div className="grid gap-3">
                      {group.fields.map((field) => (
                        <div key={field.id}>
                          <label
                            htmlFor={`field-${field.id}`}
                            className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                          >
                            {field.label}
                          </label>
                          <FieldInputRenderer
                            field={field}
                            value={fieldValues[field.id] || ''}
                            onChange={(value) => updateFieldValue(field.id, value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionBlock>
        </>
      )}

      {isVpsHostingPackages && (
        <>
          <SectionBlock
            title="Section header"
            description="Eyebrow, headline, and intro copy above the packages grid."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {vpsHostingPackagesGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Check icon"
            description="Icon shown next to each package feature."
          >
            <div className="flex flex-wrap items-center gap-4">
              {vpsHostingCheckIconMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(vpsHostingCheckIconMedia.url)}
                    alt={vpsHostingCheckIconMedia.altText || 'Package check icon'}
                    className="h-8 w-8"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload check icon
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'check-icon',
                      altText: 'Package check icon',
                      successMessage: 'Check icon has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Packages"
            description="Pricing cards shown in the packages grid."
          >
            <div className="grid gap-4 xl:grid-cols-3">
              {vpsHostingPackagesGroups?.packageGroups.map((group) => (
                <div
                  key={group.index}
                  className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                >
                  <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                    Package {group.index}
                  </p>
                  <div className="grid gap-3">
                    {group.fields.map((field) => (
                      <div key={field.id}>
                        <label
                          htmlFor={`field-${field.id}`}
                          className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                        >
                          {field.label}
                        </label>
                        <FieldInputRenderer
                          field={field}
                          value={fieldValues[field.id] || ''}
                          onChange={(value) => updateFieldValue(field.id, value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isVpsHostingIncluded && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title shown above the included items grid."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {vpsHostingIncludedGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Check icon"
            description="Icon shown next to each included item."
          >
            <div className="flex flex-wrap items-center gap-4">
              {vpsHostingCheckIconMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(vpsHostingCheckIconMedia.url)}
                    alt={vpsHostingCheckIconMedia.altText || 'Included check icon'}
                    className="h-8 w-8"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload check icon
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'check-icon',
                      altText: 'Included check icon',
                      successMessage: 'Check icon has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Included items"
            description="List of items included with every VPS."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {vpsHostingIncludedGroups?.itemFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isVpsHostingRequestToResponse && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title shown above the steps grid."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {vpsHostingRequestToResponseGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Steps"
            description="Cards shown in the request to response process grid."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {vpsHostingRequestToResponseGroups?.stepGroups.map((group) => (
                <div
                  key={group.index}
                  className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                >
                  <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                    Step {group.index}
                  </p>
                  <div className="grid gap-3">
                    {group.fields.map((field) => (
                      <div key={field.id}>
                        <label
                          htmlFor={`field-${field.id}`}
                          className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                        >
                          {field.label}
                        </label>
                        <FieldInputRenderer
                          field={field}
                          value={fieldValues[field.id] || ''}
                          onChange={(value) => updateFieldValue(field.id, value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isVpsHostingAccessControl && (
        <>
          <SectionBlock
            title="Section content"
            description="Headline, supporting copy, and disclaimer note."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {vpsHostingAccessControlFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Note icon"
            description="Icon shown next to the disclaimer note."
          >
            <div className="flex flex-wrap items-center gap-4">
              {vpsHostingNoteIconMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(vpsHostingNoteIconMedia.url)}
                    alt={vpsHostingNoteIconMedia.altText || 'Note icon'}
                    className="h-8 w-8"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload note icon
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'note-icon',
                      altText: 'Note icon',
                      successMessage: 'Note icon has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Feature image"
            description="Image shown on the right side of the access control section."
          >
            <div className="flex flex-wrap items-center gap-4">
              {featureImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(featureImageMedia.url)}
                    alt={featureImageMedia.altText || 'Access control image'}
                    className="h-40 w-auto max-w-full object-cover sm:h-48"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload feature image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-image',
                      altText: 'Access control image',
                      successMessage: 'Feature image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isVpsHostingFaq && (
        <>
          <SectionBlock
            title="Section header"
            description="Headline and subtitle shown above the FAQ list."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {vpsHostingFaqGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="FAQ items"
            description="Questions and answers shown in the accordion."
          >
            <VpsHostingFaqEditor
              sectionId={section.id}
              fields={fields}
              fieldValues={fieldValues}
              updateFieldValue={updateFieldValue}
              faqGroups={vpsHostingFaqGroups?.faqGroups || []}
              onFieldsReload={reloadFields}
              isSaving={isSaving}
            />
          </SectionBlock>
        </>
      )}

      {isInsightsHero && (
        <>
          <SectionBlock
            title="Hero image"
            description="Background image shown behind the insights hero headline."
          >
            <div className="flex flex-wrap items-center gap-4">
              {heroImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(heroImageMedia.url)}
                    alt={heroImageMedia.altText || 'Insights hero image'}
                    className="h-32 w-auto max-w-full object-cover sm:h-40"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload hero image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'hero-image',
                      altText: 'Insights hero image',
                      successMessage: 'Hero image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Hero content"
            description="Eyebrow label, headline, and supporting copy."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {insightsHeroFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isInsightsPillars && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title shown above the three pillar cards."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {insightsPillarsGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Pillar cards"
            description="Three cards linking to market analysis, news, and the economic calendar."
          >
            <div className="grid gap-4 xl:grid-cols-3">
              {insightsPillarsGroups?.itemGroups.map((group) => {
                const iconMedia = media.find(
                  (item) => item.groupKey === `item-${group.index}-icon`
                );

                return (
                  <div
                    key={group.index}
                    className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                  >
                    <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                      Pillar {group.index}
                    </p>
                    {iconMedia ? (
                      <img
                        src={resolveMediaUrl(iconMedia.url)}
                        alt={iconMedia.altText || `Pillar ${group.index} icon`}
                        className="mb-3 h-8 w-8"
                      />
                    ) : null}
                    <label className="mb-4 inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-[12px] font-medium text-[#130705] hover:bg-[#faf8f4]">
                      Upload icon
                      <input
                        type="file"
                        accept="image/*,.svg"
                        className="hidden"
                        onChange={(event) =>
                          handleLogoUpload(event, {
                            groupKey: `item-${group.index}-icon`,
                            altText: `Pillar ${group.index} icon`,
                            successMessage: `Pillar ${group.index} icon has been updated successfully.`,
                          })
                        }
                        disabled={isSaving}
                      />
                    </label>
                    <div className="grid gap-3">
                      {group.fields.map((field) => (
                        <div key={field.id}>
                          <label
                            htmlFor={`field-${field.id}`}
                            className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                          >
                            {field.label}
                          </label>
                          <FieldInputRenderer
                            field={field}
                            value={fieldValues[field.id] || ''}
                            onChange={(value) => updateFieldValue(field.id, value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionBlock>
        </>
      )}

      {isInsightsFeatured && (
        <>
          <SectionBlock
            title="Section header"
            description="Eyebrow, title, and header call-to-action above the featured card."
          >
            {insightsFeaturedHeaderCtaUrlField &&
            fieldValues[insightsFeaturedHeaderCtaUrlField.id] ? (
              <p className="mb-4 truncate text-[11px] text-[#a8a098]">
                Header CTA route: {fieldValues[insightsFeaturedHeaderCtaUrlField.id]}
              </p>
            ) : null}
            <div className="grid gap-4 sm:max-w-2xl">
              {insightsFeaturedHeaderFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
              {insightsFeaturedHeaderCtaUrlField ? (
                <div>
                  <label
                    htmlFor={`field-${insightsFeaturedHeaderCtaUrlField.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {insightsFeaturedHeaderCtaUrlField.label}
                  </label>
                  <FieldInputRenderer
                    field={insightsFeaturedHeaderCtaUrlField}
                    value={fieldValues[insightsFeaturedHeaderCtaUrlField.id] || ''}
                    onChange={(value) =>
                      updateFieldValue(insightsFeaturedHeaderCtaUrlField.id, value)
                    }
                  />
                </div>
              ) : null}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Featured card"
            description="Tag, title, excerpt, and link for the highlighted analysis card."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {insightsFeaturedCardFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Featured image"
            description="Image shown on the right side of the featured card."
          >
            <div className="flex flex-wrap items-center gap-4">
              {featureImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(featureImageMedia.url)}
                    alt={featureImageMedia.altText || 'Featured analysis image'}
                    className="h-32 w-auto max-w-full object-cover sm:h-40"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload featured image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-image',
                      altText: 'Featured analysis image',
                      successMessage: 'Featured image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isInsightsNews && (
        <SectionBlock
          title="News section header"
          description="Header copy for the news grid. Article cards are loaded from market analysis posts."
        >
          {insightsNewsCtaUrlField && fieldValues[insightsNewsCtaUrlField.id] ? (
            <p className="mb-4 truncate text-[11px] text-[#a8a098]">
              CTA route: {fieldValues[insightsNewsCtaUrlField.id]}
            </p>
          ) : null}
          <div className="grid gap-4 sm:max-w-2xl">
            {insightsNewsFields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
            {insightsNewsCtaUrlField ? (
              <div>
                <label
                  htmlFor={`field-${insightsNewsCtaUrlField.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {insightsNewsCtaUrlField.label}
                </label>
                <FieldInputRenderer
                  field={insightsNewsCtaUrlField}
                  value={fieldValues[insightsNewsCtaUrlField.id] || ''}
                  onChange={(value) => updateFieldValue(insightsNewsCtaUrlField.id, value)}
                />
              </div>
            ) : null}
          </div>
        </SectionBlock>
      )}

      {isInsightsSubscribe && (
        <SectionBlock
          title="Subscribe section"
          description="Newsletter headline, supporting copy, button label, and privacy text."
        >
          <div className="grid gap-4 sm:max-w-2xl">
            {insightsSubscribeFields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
          </div>
        </SectionBlock>
      )}

      {isContactHero && (
        <>
          <SectionBlock
            title="Hero image"
            description="Background image shown behind the contact headline."
          >
            <div className="flex flex-wrap items-center gap-4">
              {heroImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(heroImageMedia.url)}
                    alt={heroImageMedia.altText || 'Contact hero image'}
                    className="h-32 w-auto max-w-full object-cover sm:h-40"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload hero image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'hero-image',
                      altText: 'Contact hero image',
                      successMessage: 'Hero image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Hero content"
            description="Eyebrow label, headline, and supporting copy."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {contactHeroFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isContactForm && (
        <SectionBlock
          title="Contact form copy"
          description="Intro, privacy agreement, button label, and success message. Form fields stay as they are."
        >
          <div className="grid gap-4 sm:max-w-2xl">
            {contactFormFields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
          </div>
        </SectionBlock>
      )}

      {isContactSpeak && (
        <>
          <SectionBlock
            title="Section content"
            description="Eyebrow, title, and supporting copy."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {contactSpeakGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Contact details"
            description="Phone and email shown next to the section copy."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {contactSpeakGroups?.itemGroups.map((group) => {
                const iconMedia = media.find(
                  (item) => item.groupKey === `item-${group.index}-icon`
                );

                return (
                  <div
                    key={group.index}
                    className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                  >
                    <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                      Item {group.index}
                    </p>
                    {iconMedia ? (
                      <img
                        src={resolveMediaUrl(iconMedia.url)}
                        alt={iconMedia.altText || `Item ${group.index} icon`}
                        className="mb-3 h-8 w-8"
                      />
                    ) : null}
                    <label className="mb-4 inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-[12px] font-medium text-[#130705] hover:bg-[#faf8f4]">
                      Upload icon
                      <input
                        type="file"
                        accept="image/*,.svg"
                        className="hidden"
                        onChange={(event) =>
                          handleLogoUpload(event, {
                            groupKey: `item-${group.index}-icon`,
                            altText: `Item ${group.index} icon`,
                            successMessage: `Item ${group.index} icon has been updated successfully.`,
                          })
                        }
                        disabled={isSaving}
                      />
                    </label>
                    <div className="grid gap-3">
                      {group.fields.map((field) => (
                        <div key={field.id}>
                          <label
                            htmlFor={`field-${field.id}`}
                            className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                          >
                            {field.label}
                          </label>
                          <FieldInputRenderer
                            field={field}
                            value={fieldValues[field.id] || ''}
                            onChange={(value) => updateFieldValue(field.id, value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Feature image"
            description="Image shown below the contact details."
          >
            <div className="flex flex-wrap items-center gap-4">
              {featureImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(featureImageMedia.url)}
                    alt={featureImageMedia.altText || 'Speak with our team image'}
                    className="h-32 w-auto max-w-full object-cover sm:h-40"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-image',
                      altText: 'Speak with our team image',
                      successMessage: 'Image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isEconomicCalendarHero && (
        <SectionBlock
          title="Hero content"
          description="Eyebrow label, headline, and supporting copy."
        >
          <div className="grid gap-4 sm:max-w-2xl">
            {economicCalendarHeroFields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
          </div>
        </SectionBlock>
      )}

      {isEconomicCalendarEventImpact && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title shown above the event impact cards."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {economicCalendarEventImpactGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Impact cards"
            description="Cards explaining high, medium, and low impact events."
          >
            <div className="grid gap-4 xl:grid-cols-3">
              {economicCalendarEventImpactGroups?.itemGroups.map((group) => {
                const iconMedia = media.find(
                  (item) => item.groupKey === `item-${group.index}-icon`
                );

                return (
                  <div
                    key={group.index}
                    className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                  >
                    <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                      Card {group.index}
                    </p>
                    {iconMedia ? (
                      <img
                        src={resolveMediaUrl(iconMedia.url)}
                        alt={iconMedia.altText || `Card ${group.index} icon`}
                        className="mb-3 h-8 w-8"
                      />
                    ) : null}
                    <label className="mb-4 inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-[12px] font-medium text-[#130705] hover:bg-[#faf8f4]">
                      Upload icon
                      <input
                        type="file"
                        accept="image/*,.svg"
                        className="hidden"
                        onChange={(event) =>
                          handleLogoUpload(event, {
                            groupKey: `item-${group.index}-icon`,
                            altText: `Card ${group.index} icon`,
                            successMessage: `Card ${group.index} icon has been updated successfully.`,
                          })
                        }
                        disabled={isSaving}
                      />
                    </label>
                    <div className="grid gap-3">
                      {group.fields.map((field) => (
                        <div key={field.id}>
                          <label
                            htmlFor={`field-${field.id}`}
                            className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                          >
                            {field.label}
                          </label>
                          <FieldInputRenderer
                            field={field}
                            value={fieldValues[field.id] || ''}
                            onChange={(value) => updateFieldValue(field.id, value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionBlock>
        </>
      )}

      {isEconomicCalendarMacroHighlights && (
        <SectionBlock
          title="Macro highlights section"
          description="Newsletter headline, supporting copy, button label, and privacy text."
        >
          <div className="grid gap-4 sm:max-w-2xl">
            {economicCalendarMacroHighlightsFields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
          </div>
        </SectionBlock>
      )}

      {isMarketAnalysisHero && (
        <SectionBlock
          title="Hero content"
          description="Eyebrow label, headline, and supporting copy."
        >
          <div className="grid gap-4 sm:max-w-2xl">
            {marketAnalysisHeroFields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
          </div>
        </SectionBlock>
      )}

      {isMarketAnalysisPostsSettings && (
        <SectionBlock
          title="Post categories"
          description="One category per line. Include All as the first option."
        >
          <div className="grid gap-4 sm:max-w-2xl">
            {marketAnalysisPostsSettingsFields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
          </div>
        </SectionBlock>
      )}

      {isMarketAnalysisMarketInsights && (
        <SectionBlock
          title="Newsletter section"
          description="Copy and subscribe button for the market insights panel."
        >
          {marketAnalysisMarketInsightsPrivacyUrlField &&
          fieldValues[marketAnalysisMarketInsightsPrivacyUrlField.id] ? (
            <p className="mb-4 truncate text-[11px] text-[#a8a098]">
              Privacy route: {fieldValues[marketAnalysisMarketInsightsPrivacyUrlField.id]}
            </p>
          ) : null}
          <div className="grid gap-4 sm:max-w-2xl">
            {marketAnalysisMarketInsightsFields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
            {marketAnalysisMarketInsightsPrivacyUrlField ? (
              <div>
                <label
                  htmlFor={`field-${marketAnalysisMarketInsightsPrivacyUrlField.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {marketAnalysisMarketInsightsPrivacyUrlField.label}
                </label>
                <FieldInputRenderer
                  field={marketAnalysisMarketInsightsPrivacyUrlField}
                  value={fieldValues[marketAnalysisMarketInsightsPrivacyUrlField.id] || ''}
                  onChange={(value) =>
                    updateFieldValue(marketAnalysisMarketInsightsPrivacyUrlField.id, value)
                  }
                />
              </div>
            ) : null}
          </div>
        </SectionBlock>
      )}

      {isMarketAnalysisPostMeta && (
        <>
          <SectionBlock
            title="Featured post"
            description="Choose whether this article appears in the main featured card."
          >
            <MarketAnalysisPostFeaturedEditor
              pageId={pageId}
              isFeatured={(fieldValues[marketAnalysisPostFeaturedField?.id] || 'false') === 'true'}
              onFeaturedChange={reloadFields}
            />
          </SectionBlock>

          <SectionBlock
            title="Post metadata"
            description="Category, title, excerpt, author, and publish date."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {marketAnalysisPostFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Post image"
            description="Main image used in cards and the article hero."
          >
            <div className="flex flex-wrap items-center gap-4">
              {marketAnalysisPostImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(marketAnalysisPostImageMedia.url)}
                    alt={marketAnalysisPostImageMedia.altText || 'Post image'}
                    className="h-32 w-auto max-w-full object-cover sm:h-40"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload post image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'post-image',
                      altText: 'Post image',
                      successMessage: 'Post image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Author avatar"
            description="Avatar shown next to the author name."
          >
            <div className="flex flex-wrap items-center gap-4">
              {marketAnalysisPostAvatarMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(marketAnalysisPostAvatarMedia.url)}
                    alt={marketAnalysisPostAvatarMedia.altText || 'Author avatar'}
                    className="h-16 w-16 rounded-full object-cover"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload avatar
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'avatar',
                      altText: 'Author avatar',
                      successMessage: 'Avatar has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isMarketAnalysisPostContent && (
        <SectionBlock
          title="Article content"
          description="Lead paragraph and article body. Separate paragraphs with a blank line."
        >
          <div className="grid gap-4 sm:max-w-2xl">
            {marketAnalysisPostContentFields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
          </div>
        </SectionBlock>
      )}

      {isWealthPageHero && (
        <>
          <SectionBlock
            title="Background image"
            description="Full-width background image behind the hero headline."
          >
            <div className="flex flex-wrap items-center gap-4">
              {backgroundMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(backgroundMedia.url)}
                    alt={backgroundMedia.altText || 'Hero background'}
                    className="h-32 w-auto max-w-full object-cover sm:h-40"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload background image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'background',
                      altText: 'Wealth and savings hero background',
                      successMessage: 'Hero background image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Hero content"
            description="Eyebrow label, headline, and supporting copy."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {wealthPageHeroFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isWealthApproach && (
        <>
          <SectionBlock
            title="Section content"
            description="Headline and supporting paragraphs."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {globalCompaniesGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
              {globalCompaniesGroups?.paragraphFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Feature image"
            description="Image shown on the right side of the section."
          >
            <div className="flex flex-wrap items-center gap-4">
              {featureImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(featureImageMedia.url)}
                    alt={featureImageMedia.altText || 'Approach feature image'}
                    className="h-40 w-auto max-w-full object-cover sm:h-48"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload feature image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-image',
                      altText: 'Approach feature image',
                      successMessage: 'Feature image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isWealthProducts && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title and link text shown on each product card."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {wealthProductGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Products"
            description="Product cards shown in the grid."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {wealthProductGroups?.productGroups.map((group) => (
                <div
                  key={group.index}
                  className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                >
                  <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                    Product {group.index}
                  </p>
                  <div className="grid gap-3">
                    {group.fields.map((field) => (
                      <div key={field.id}>
                        <label
                          htmlFor={`field-${field.id}`}
                          className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                        >
                          {field.label}
                        </label>
                        <FieldInputRenderer
                          field={field}
                          value={fieldValues[field.id] || ''}
                          onChange={(value) => updateFieldValue(field.id, value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isWealthSecurity && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title shown above the security cards grid."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {wealthSecurityGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Security cards"
            description="Cards shown in the security grid."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {wealthSecurityGroups?.itemGroups.map((group) => {
                const iconMedia = media.find(
                  (item) => item.groupKey === `item-${group.index}-icon`
                );

                return (
                  <div
                    key={group.index}
                    className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                  >
                    <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                      Card {group.index}
                    </p>
                    {iconMedia ? (
                      <img
                        src={resolveMediaUrl(iconMedia.url)}
                        alt={iconMedia.altText || `Security card ${group.index} icon`}
                        className="mb-3 h-8 w-8"
                      />
                    ) : null}
                    <label className="mb-4 inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-[12px] font-medium text-[#130705] hover:bg-[#faf8f4]">
                      Upload icon
                      <input
                        type="file"
                        accept="image/*,.svg"
                        className="hidden"
                        onChange={(event) =>
                          handleLogoUpload(event, {
                            groupKey: `item-${group.index}-icon`,
                            altText: `Security card ${group.index} icon`,
                            successMessage: `Security card ${group.index} icon has been updated successfully.`,
                          })
                        }
                        disabled={isSaving}
                      />
                    </label>
                    <div className="grid gap-3">
                      {group.fields.map((field) => (
                        <div key={field.id}>
                          <label
                            htmlFor={`field-${field.id}`}
                            className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                          >
                            {field.label}
                          </label>
                          <FieldInputRenderer
                            field={field}
                            value={fieldValues[field.id] || ''}
                            onChange={(value) => updateFieldValue(field.id, value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionBlock>
        </>
      )}

      {isSolutionDetailHero && (
        <>
          <SectionBlock
            title="Background image"
            description="Hero image shown on the right side of the solution detail page."
          >
            <div className="flex flex-wrap items-center gap-4">
              {backgroundMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(backgroundMedia.url)}
                    alt={backgroundMedia.altText || 'Hero background'}
                    className="h-32 w-auto max-w-full object-cover sm:h-40"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload background image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'background',
                      altText: 'Solution detail hero background',
                      successMessage: 'Hero background image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Hero content"
            description="Category label, headline, supporting copy, and call-to-action."
          >
            {solutionDetailHeroCtaUrlField && fieldValues[solutionDetailHeroCtaUrlField.id] ? (
              <p className="mb-4 truncate text-[11px] text-[#a8a098]">
                CTA route: {fieldValues[solutionDetailHeroCtaUrlField.id]}
              </p>
            ) : null}
            <div className="grid gap-4 sm:max-w-2xl">
              {solutionDetailHeroFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isSolutionDetailView && (
        <>
          <SectionBlock
            title="View content"
            description="Headline, description, and bullet list items."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {solutionDetailViewFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Check icon"
            description="Icon shown next to each bullet item."
          >
            <div className="flex flex-wrap items-center gap-4">
              {checkIconMedia ? (
                <div className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-5 py-4">
                  <img
                    src={resolveMediaUrl(checkIconMedia.url)}
                    alt={checkIconMedia.altText || 'Check icon'}
                    className="h-8 w-8"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload check icon
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'check-icon',
                      altText: 'Check icon',
                      successMessage: 'Check icon has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isSolutionDetailWhatWeDeliver && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title shown above the deliverables grid."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {deliverableGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Deliverables"
            description="Cards shown in the deliverables grid."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {deliverableGroups?.deliverableGroups.map((group) => {
                const iconMedia = media.find(
                  (item) => item.groupKey === `deliverable-${group.index}-icon`
                );

                return (
                  <div
                    key={group.index}
                    className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                  >
                    <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                      Deliverable {group.index}
                    </p>
                    {iconMedia ? (
                      <img
                        src={resolveMediaUrl(iconMedia.url)}
                        alt={iconMedia.altText || `Deliverable ${group.index} icon`}
                        className="mb-3 h-8 w-8"
                      />
                    ) : null}
                    <label className="mb-4 inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-[12px] font-medium text-[#130705] hover:bg-[#faf8f4]">
                      Upload icon
                      <input
                        type="file"
                        accept="image/*,.svg"
                        className="hidden"
                        onChange={(event) =>
                          handleLogoUpload(event, {
                            groupKey: `deliverable-${group.index}-icon`,
                            altText: `Deliverable ${group.index} icon`,
                            successMessage: `Deliverable ${group.index} icon has been updated successfully.`,
                          })
                        }
                        disabled={isSaving}
                      />
                    </label>
                    <div className="grid gap-3">
                      {group.fields.map((field) => (
                        <div key={field.id}>
                          <label
                            htmlFor={`field-${field.id}`}
                            className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                          >
                            {field.label}
                          </label>
                          <FieldInputRenderer
                            field={field}
                            value={fieldValues[field.id] || ''}
                            onChange={(value) => updateFieldValue(field.id, value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionBlock>
        </>
      )}

      {isSolutionDetailWhyChoose && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title and supporting copy shown above the accordion list."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {whyChooseGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Reasons"
            description="Accordion items shown in the left column."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {whyChooseGroups?.itemGroups.map((group) => (
                <WhoWeWorkWithClientEditor
                  key={group.index}
                  index={group.index}
                  fields={group.fields}
                  fieldValues={fieldValues}
                  onFieldChange={updateFieldValue}
                />
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Feature image"
            description="Image shown on the right side of the section."
          >
            <div className="flex flex-wrap items-center gap-4">
              {featureImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(featureImageMedia.url)}
                    alt={featureImageMedia.altText || 'Why choose feature image'}
                    className="h-40 w-auto max-w-full object-cover sm:h-48"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload feature image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-image',
                      altText: 'Why choose feature image',
                      successMessage: 'Feature image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isWealthDetailHero && (
        <>
          <SectionBlock
            title="Hero image"
            description="Image shown on the right side of the wealth product page."
          >
            <div className="flex flex-wrap items-center gap-4">
              {heroImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(heroImageMedia.url)}
                    alt={heroImageMedia.altText || 'Wealth product hero image'}
                    className="h-32 w-auto max-w-full object-cover sm:h-40"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload hero image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'hero-image',
                      altText: 'Wealth product hero image',
                      successMessage: 'Hero image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Hero content"
            description="Category label, headline, supporting copy, and call-to-action."
          >
            {wealthDetailHeroCtaUrlField && fieldValues[wealthDetailHeroCtaUrlField.id] ? (
              <p className="mb-4 truncate text-[11px] text-[#a8a098]">
                CTA route: {fieldValues[wealthDetailHeroCtaUrlField.id]}
              </p>
            ) : null}
            <div className="grid gap-4 sm:max-w-2xl">
              {wealthDetailHeroFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isWealthDetailEarn && (
        <>
          <SectionBlock
            title="Section content"
            description="Headline and supporting paragraphs."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {globalCompaniesGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
              {globalCompaniesGroups?.paragraphFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Feature image"
            description="Image shown on the left side of the earn section."
          >
            <div className="flex flex-wrap items-center gap-4">
              {featureImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(featureImageMedia.url)}
                    alt={featureImageMedia.altText || 'Earn section image'}
                    className="h-40 w-auto max-w-full object-cover sm:h-48"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload feature image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-image',
                      altText: 'Earn section image',
                      successMessage: 'Feature image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isWealthDetailCoreParameters && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title shown above the core parameters grid."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {wealthDetailParameterGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Parameters"
            description="Cards shown in the core parameters grid."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {wealthDetailParameterGroups?.parameterGroups.map((group) => {
                const iconMedia = media.find(
                  (item) => item.groupKey === `parameter-${group.index}-icon`
                );

                return (
                  <div
                    key={group.index}
                    className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                  >
                    <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                      Parameter {group.index}
                    </p>
                    {iconMedia ? (
                      <img
                        src={resolveMediaUrl(iconMedia.url)}
                        alt={iconMedia.altText || `Parameter ${group.index} icon`}
                        className="mb-3 h-8 w-8"
                      />
                    ) : null}
                    <label className="mb-4 inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-[12px] font-medium text-[#130705] hover:bg-[#faf8f4]">
                      Upload icon
                      <input
                        type="file"
                        accept="image/*,.svg"
                        className="hidden"
                        onChange={(event) =>
                          handleLogoUpload(event, {
                            groupKey: `parameter-${group.index}-icon`,
                            altText: `Parameter ${group.index} icon`,
                            successMessage: `Parameter ${group.index} icon has been updated successfully.`,
                          })
                        }
                        disabled={isSaving}
                      />
                    </label>
                    <div className="grid gap-3">
                      {group.fields.map((field) => (
                        <div key={field.id}>
                          <label
                            htmlFor={`field-${field.id}`}
                            className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                          >
                            {field.label}
                          </label>
                          <FieldInputRenderer
                            field={field}
                            value={fieldValues[field.id] || ''}
                            onChange={(value) => updateFieldValue(field.id, value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionBlock>
        </>
      )}

      {isWealthDetailSavings && (
        <>
          <SectionBlock
            title="Quote"
            description="Savings quote shown in the panel."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {wealthDetailSavingsFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Eagle illustration"
            description="Decorative eagle image shown beside the quote."
          >
            <div className="flex flex-wrap items-center gap-4">
              {riskEagleMedia ? (
                <div className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-5 py-4">
                  <img
                    src={resolveMediaUrl(riskEagleMedia.url)}
                    alt={riskEagleMedia.altText || 'Savings eagle illustration'}
                    className="h-16 w-auto"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload eagle image
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'eagle-image',
                      altText: 'Savings eagle illustration',
                      successMessage: 'Eagle image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isWealthDetailAccountFeatures && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title shown above the account features list."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {wealthDetailAccountFeatureGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Features"
            description="Label and value pairs shown in the account features panel."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {wealthDetailAccountFeatureGroups?.featureGroups.map((group) => (
                <div
                  key={group.index}
                  className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                >
                  <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                    Feature {group.index}
                  </p>
                  <div className="grid gap-3">
                    {group.fields.map((field) => (
                      <div key={field.id}>
                        <label
                          htmlFor={`field-${field.id}`}
                          className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                        >
                          {field.label}
                        </label>
                        <FieldInputRenderer
                          field={field}
                          value={fieldValues[field.id] || ''}
                          onChange={(value) => updateFieldValue(field.id, value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isMarketDetailHero && (
        <>
          <SectionBlock
            title="Hero image"
            description="Graphic shown on the right side of the market detail page."
          >
            <div className="flex flex-wrap items-center gap-4">
              {heroImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(heroImageMedia.url)}
                    alt={heroImageMedia.altText || 'Market hero image'}
                    className="h-32 w-auto max-w-full object-cover sm:h-40"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload hero image
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'hero-image',
                      altText: 'Market hero image',
                      successMessage: 'Hero image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Hero content"
            description="Category label, headline, supporting copy, and call-to-action."
          >
            {marketDetailHeroCtaUrlField && fieldValues[marketDetailHeroCtaUrlField.id] ? (
              <p className="mb-4 truncate text-[11px] text-[#a8a098]">
                CTA route: {fieldValues[marketDetailHeroCtaUrlField.id]}
              </p>
            ) : null}
            <div className="grid gap-4 sm:max-w-2xl">
              {marketDetailHeroFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isMarketDetailGlobalCompanies && (
        <>
          <SectionBlock
            title="Section content"
            description="Headline and supporting paragraphs."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {globalCompaniesGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
              {globalCompaniesGroups?.paragraphFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Feature image"
            description="Image shown on the right side of the section."
          >
            <div className="flex flex-wrap items-center gap-4">
              {featureImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(featureImageMedia.url)}
                    alt={featureImageMedia.altText || 'Global companies feature image'}
                    className="h-40 w-auto max-w-full object-cover sm:h-48"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload feature image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-image',
                      altText: 'Global companies feature image',
                      successMessage: 'Feature image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isMarketDetailMarketSegments && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title shown above the market segments grid."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {marketSegmentGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Segments"
            description="Cards shown in the market segments grid."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {marketSegmentGroups?.segmentGroups.map((group) => {
                const iconMedia = media.find(
                  (item) => item.groupKey === `segment-${group.index}-icon`
                );

                return (
                  <div
                    key={group.index}
                    className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                  >
                    <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                      Segment {group.index}
                    </p>
                    {iconMedia ? (
                      <img
                        src={resolveMediaUrl(iconMedia.url)}
                        alt={iconMedia.altText || `Segment ${group.index} icon`}
                        className="mb-3 h-8 w-8"
                      />
                    ) : null}
                    <label className="mb-4 inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-[12px] font-medium text-[#130705] hover:bg-[#faf8f4]">
                      Upload icon
                      <input
                        type="file"
                        accept="image/*,.svg"
                        className="hidden"
                        onChange={(event) =>
                          handleLogoUpload(event, {
                            groupKey: `segment-${group.index}-icon`,
                            altText: `Segment ${group.index} icon`,
                            successMessage: `Segment ${group.index} icon has been updated successfully.`,
                          })
                        }
                        disabled={isSaving}
                      />
                    </label>
                    <div className="grid gap-3">
                      {group.fields.map((field) => (
                        <div key={field.id}>
                          <label
                            htmlFor={`field-${field.id}`}
                            className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                          >
                            {field.label}
                          </label>
                          <FieldInputRenderer
                            field={field}
                            value={fieldValues[field.id] || ''}
                            onChange={(value) => updateFieldValue(field.id, value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionBlock>
        </>
      )}

      {isMarketDetailFeaturedCompanies && (
        <>
          <SectionBlock
            title="Section heading"
            description="Title shown above the featured companies grid."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {featuredCompanyGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Companies"
            description="Featured company cards shown in the grid."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {featuredCompanyGroups?.companyGroups.map((group) => {
                const logoMedia = media.find(
                  (item) => item.groupKey === `company-${group.index}-logo`
                );

                return (
                  <div
                    key={group.index}
                    className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
                  >
                    <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                      Company {group.index}
                    </p>
                    {logoMedia ? (
                      <img
                        src={resolveMediaUrl(logoMedia.url)}
                        alt={logoMedia.altText || `Company ${group.index} logo`}
                        className="mb-3 h-8 w-8"
                      />
                    ) : null}
                    <label className="mb-4 inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-[12px] font-medium text-[#130705] hover:bg-[#faf8f4]">
                      Upload logo
                      <input
                        type="file"
                        accept="image/*,.svg"
                        className="hidden"
                        onChange={(event) =>
                          handleLogoUpload(event, {
                            groupKey: `company-${group.index}-logo`,
                            altText: `Company ${group.index} logo`,
                            successMessage: `Company ${group.index} logo has been updated successfully.`,
                          })
                        }
                        disabled={isSaving}
                      />
                    </label>
                    <div className="grid gap-3">
                      {group.fields.map((field) => (
                        <div key={field.id}>
                          <label
                            htmlFor={`field-${field.id}`}
                            className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                          >
                            {field.label}
                          </label>
                          <FieldInputRenderer
                            field={field}
                            value={fieldValues[field.id] || ''}
                            onChange={(value) => updateFieldValue(field.id, value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionBlock>
        </>
      )}

      {isMarketDetailMarketAccess && (
        <>
          <SectionBlock
            title="Section content"
            description="Headline, description, bullet items, and call-to-action."
          >
            {marketDetailAccessCtaUrlField && fieldValues[marketDetailAccessCtaUrlField.id] ? (
              <p className="mb-4 truncate text-[11px] text-[#a8a098]">
                CTA route: {fieldValues[marketDetailAccessCtaUrlField.id]}
              </p>
            ) : null}
            <div className="grid gap-4 sm:max-w-2xl">
              {marketDetailAccessFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Check icon"
            description="Icon shown next to each bullet item."
          >
            <div className="flex flex-wrap items-center gap-4">
              {checkIconMedia ? (
                <div className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-5 py-4">
                  <img
                    src={resolveMediaUrl(checkIconMedia.url)}
                    alt={checkIconMedia.altText || 'Check icon'}
                    className="h-8 w-8"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload check icon
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'check-icon',
                      altText: 'Check icon',
                      successMessage: 'Check icon has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Feature image"
            description="Image shown on the right side of the section."
          >
            <div className="flex flex-wrap items-center gap-4">
              {featureImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(featureImageMedia.url)}
                    alt={featureImageMedia.altText || 'Market access feature image'}
                    className="h-40 w-auto max-w-full object-cover sm:h-48"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload feature image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-image',
                      altText: 'Market access feature image',
                      successMessage: 'Feature image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isMarketDetailRisk && (
        <>
          <SectionBlock
            title="Risk content"
            description="Headline and risk disclaimer text."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {marketDetailRiskFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Eagle illustration"
            description="Decorative eagle image shown in the risk panel."
          >
            <div className="flex flex-wrap items-center gap-4">
              {riskEagleMedia ? (
                <div className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-5 py-4">
                  <img
                    src={resolveMediaUrl(riskEagleMedia.url)}
                    alt={riskEagleMedia.altText || 'Risk eagle illustration'}
                    className="h-16 w-auto"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload eagle image
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'eagle-image',
                      altText: 'Risk eagle illustration',
                      successMessage: 'Eagle image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isTtg && (
        <>
          <SectionBlock
            title="Quote"
            description="The main blockquote shown on the left side of the section."
          >
            {fields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
          </SectionBlock>

          <SectionBlock
            title="Eagle illustration"
            description="Decorative eagle image shown beside the quote."
          >
            <div className="flex flex-wrap items-center gap-4">
              {eagleMedia ? (
                <div className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-5 py-4">
                  <img
                    src={resolveMediaUrl(eagleMedia.url)}
                    alt={eagleMedia.altText || 'Eagle illustration'}
                    className="h-16 w-auto"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload eagle image
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'eagle',
                      isPrimary: false,
                      altText: 'Eagle illustration',
                      successMessage: 'Eagle image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Feature cards"
            description="The three cards shown on the right — Tailored, Trusted, and Global."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {cardMedia.map((item, index) => (
                <FeatureCardEditor
                  key={item.id}
                  item={item}
                  index={index + 1}
                  values={mediaValues[item.id] || { title: '', subtitle: '' }}
                  isUploading={uploadingItemId === item.id}
                  onIconUpload={(event) => handleCardIconUpload(item, event)}
                  onChange={(values) =>
                    setMediaValues((current) => ({ ...current, [item.id]: values }))
                  }
                />
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isWhatWeDo && (
        <>
          <SectionBlock
            title="Section header"
            description="Eyebrow label and main heading above the service rows."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {fields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Service rows"
            description="Investment, Consulting, and Wealth rows with image, icon, copy, and layout direction."
          >
            <div className="grid gap-4">
              {rowMedia.map((item, index) => (
                <WhatWeDoRowEditor
                  key={item.id}
                  item={item}
                  index={index + 1}
                  values={
                    mediaValues[item.id] || {
                      title: '',
                      description: '',
                      linkUrl: '',
                      reverse: false,
                    }
                  }
                  isUploadingImage={uploadingRowImageId === item.id}
                  isUploadingIcon={uploadingRowIconId === item.id}
                  onImageUpload={(event) => handleRowImageUpload(item, event)}
                  onIconUpload={(event) => handleRowIconUpload(item, event)}
                  onChange={(values) =>
                    setMediaValues((current) => ({ ...current, [item.id]: values }))
                  }
                />
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isGlobalMarkets && (
        <>
          <SectionBlock
            title="Section header"
            description="Eyebrow, title, and lead text above the market cards. Use a new line in the title for a line break."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {fields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Market cards"
            description="Each card links to a market page. Names and example items can be edited; routes are fixed."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {marketMedia.map((item, index) => (
                <GlobalMarketCardEditor
                  key={item.id}
                  item={item}
                  index={index + 1}
                  values={mediaValues[item.id] || { name: '', items: '', linkUrl: '' }}
                  isUploading={uploadingItemId === item.id}
                  onIconUpload={(event) => handleMarketIconUpload(item, event)}
                  onChange={(values) =>
                    setMediaValues((current) => ({ ...current, [item.id]: values }))
                  }
                />
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isWealthSavings && (
        <>
          <SectionBlock
            title="Feature panel"
            description="Left-side image and copy with call-to-action."
          >
            <div className="mb-5 flex flex-wrap items-center gap-4">
              {featureImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(featureImageMedia.url)}
                    alt={featureImageMedia.altText || 'Feature image'}
                    className="h-40 w-auto max-w-full object-cover sm:h-48"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload feature image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-image',
                      altText: 'Wealth feature image',
                      successMessage: 'Feature image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>

            {wealthCtaUrlField && fieldValues[wealthCtaUrlField.id] ? (
              <p className="mb-4 truncate text-[11px] text-[#a8a098]">
                CTA route: {fieldValues[wealthCtaUrlField.id]}
              </p>
            ) : null}

            <div className="grid gap-4 sm:max-w-2xl">
              {wealthHeaderFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Check icon"
            description="Small icon shown next to each product feature bullet."
          >
            <div className="flex flex-wrap items-center gap-4">
              {checkIconMedia ? (
                <div className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-5 py-4">
                  <img
                    src={resolveMediaUrl(checkIconMedia.url)}
                    alt={checkIconMedia.altText || 'Check icon'}
                    className="h-6 w-6 object-contain"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload check icon
                <input
                  type="file"
                  accept="image/*,.svg,.ico"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'check-icon',
                      isPrimary: false,
                      mediaType: 'icon',
                      sortOrder: '2',
                      altText: 'Feature check icon',
                      successMessage: 'Check icon has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Product cards"
            description="Wealth product cards with icon, description, feature list, and learn-more link."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {productMedia.map((item, index) => (
                <WealthProductCardEditor
                  key={item.id}
                  item={item}
                  index={index + 1}
                  values={
                    mediaValues[item.id] || {
                      name: '',
                      description: '',
                      linkUrl: '',
                      featuresText: '',
                    }
                  }
                  isUploading={uploadingItemId === item.id}
                  onIconUpload={(event) => handleProductIconUpload(item, event)}
                  onChange={(values) =>
                    setMediaValues((current) => ({ ...current, [item.id]: values }))
                  }
                />
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isHomeMarkets && (
        <>
          <SectionBlock
            title="Section intro"
            description="Heading, supporting text, and view-all CTA. Use a new line in the title for a line break."
          >
            {marketsCtaUrlField && fieldValues[marketsCtaUrlField.id] ? (
              <p className="mb-4 truncate text-[11px] text-[#a8a098]">
                CTA route: {fieldValues[marketsCtaUrlField.id]}
              </p>
            ) : null}
            <div className="grid gap-4 sm:max-w-2xl">
              {marketsIntroFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Featured posts"
            description="Blog post cards shown on the right. Categories, titles, and descriptions are editable; routes are fixed."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {marketsPostGroups?.postGroups.map((group) => (
                <MarketsPostEditor
                  key={group.index}
                  index={group.index}
                  fields={group.fields}
                  fieldValues={fieldValues}
                  onFieldChange={updateFieldValue}
                />
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isSolutionsOurExpertise && (
        <>
          <SectionBlock
            title="Section heading"
            description="Main title shown above the expertise cards grid."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {expertiseCardGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Expertise cards"
            description="Solution cards shown in the grid. Categories, titles, descriptions, and CTA text are editable; routes are fixed."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {expertiseCardGroups?.cardGroups.map((group) => (
                <ExpertiseCardEditor
                  key={group.index}
                  index={group.index}
                  fields={group.fields}
                  fieldValues={fieldValues}
                  onFieldChange={updateFieldValue}
                />
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isTailoredStrategies && (
        <>
          <SectionBlock
            title="Feature panel"
            description="Left-side copy with feature list and call-to-action. Use a new line in the title for a line break."
          >
            <div className="mb-5 flex flex-wrap items-center gap-4">
              {featureImageMedia ? (
                <div className="overflow-hidden rounded-md border border-[#e4d9c8] bg-[#faf8f4]">
                  <img
                    src={resolveMediaUrl(featureImageMedia.url)}
                    alt={featureImageMedia.altText || 'Feature image'}
                    className="h-40 w-auto max-w-full object-cover sm:h-48"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload feature image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'feature-image',
                      altText: 'Tailored strategies feature image',
                      successMessage: 'Feature image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>

            {tailoredStrategiesCtaUrlField && fieldValues[tailoredStrategiesCtaUrlField.id] ? (
              <p className="mb-4 truncate text-[11px] text-[#a8a098]">
                CTA route: {fieldValues[tailoredStrategiesCtaUrlField.id]}
              </p>
            ) : null}

            <div className="grid gap-4 sm:max-w-2xl">
              {tailoredStrategiesHeaderFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Check icon"
            description="Small icon shown next to each feature bullet."
          >
            <div className="flex flex-wrap items-center gap-4">
              {checkIconMedia ? (
                <div className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-5 py-4">
                  <img
                    src={resolveMediaUrl(checkIconMedia.url)}
                    alt={checkIconMedia.altText || 'Check icon'}
                    className="h-6 w-6 object-contain"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload check icon
                <input
                  type="file"
                  accept="image/*,.svg,.ico"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'check-icon',
                      isPrimary: false,
                      mediaType: 'icon',
                      sortOrder: '2',
                      altText: 'Feature check icon',
                      successMessage: 'Check icon has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Feature list"
            description="Bullet points shown below the main copy."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {tailoredStrategiesFeatureFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isApproachSteps && (
        <>
          <SectionBlock
            title="Section heading"
            description="Main title shown above the approach steps."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {approachStepGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Approach steps"
            description="Numbered steps shown in the process grid."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {approachStepGroups?.stepGroups.map((group) => (
                <ApproachStepEditor
                  key={group.index}
                  index={group.index}
                  fields={group.fields}
                  fieldValues={fieldValues}
                  onFieldChange={updateFieldValue}
                />
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isWhoWeWorkWith && (
        <>
          <SectionBlock
            title="Section heading"
            description="Main title shown above the client panel."
          >
            <div className="grid gap-4 sm:max-w-xl">
              {whoWeWorkWithGroups?.introFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Client types"
            description="Audience cards shown in the panel grid."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {whoWeWorkWithGroups?.clientGroups.map((group) => (
                <WhoWeWorkWithClientEditor
                  key={group.index}
                  index={group.index}
                  fields={group.fields}
                  fieldValues={fieldValues}
                  onFieldChange={updateFieldValue}
                />
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isAdvantage && (
        <>
          <SectionBlock
            title="Content"
            description="Headline and supporting copy shown in the advantage panel."
          >
            <div className="grid gap-4 sm:max-w-2xl">
              {advantageFields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Eagle illustration"
            description="Decorative eagle image shown above the headline."
          >
            <div className="flex flex-wrap items-center gap-4">
              {eagleMedia ? (
                <div className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-5 py-4">
                  <img
                    src={resolveMediaUrl(eagleMedia.url)}
                    alt={eagleMedia.altText || 'Eagle illustration'}
                    className="h-16 w-auto"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload eagle image
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'eagle',
                      isPrimary: false,
                      altText: 'Eagle illustration',
                      successMessage: 'Eagle image has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {(isLetsBuild || isTimeIsValuable || isMarketDetailStart || isWealthBuildYourHeritage || isWealthDetailGrowth || isInfrastructureBankOn || isInsightsHeritage || isEconomicCalendarBehindTheData || isContactPartner) && (
        <SectionBlock
          title="Call to action"
          description="Closing headline, supporting text, and button for the bottom of the page."
        >
          {letsBuildCtaUrlField && fieldValues[letsBuildCtaUrlField.id] ? (
            <p className="mb-4 truncate text-[11px] text-[#a8a098]">
              CTA route: {fieldValues[letsBuildCtaUrlField.id]}
            </p>
          ) : null}
          <div className="grid gap-4 sm:max-w-2xl">
            {letsBuildFields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
          </div>
        </SectionBlock>
      )}

      {isReady && (
        <SectionBlock
          title="Call to action"
          description="Closing headline, supporting text, and button for the bottom of the home page."
        >
          {readyCtaUrlField && fieldValues[readyCtaUrlField.id] ? (
            <p className="mb-4 truncate text-[11px] text-[#a8a098]">
              CTA route: {fieldValues[readyCtaUrlField.id]}
            </p>
          ) : null}
          <div className="grid gap-4 sm:max-w-2xl">
            {readyFields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
          </div>
        </SectionBlock>
      )}

      {isNavbar && (
        <>
          <SectionBlock
            title="Logo"
            description="The logo shown in the top-left of the public navbar."
          >
            <div className="flex flex-wrap items-center gap-4">
              {logoMedia ? (
                <div className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-5 py-4">
                  <img
                    src={resolveMediaUrl(logoMedia.url)}
                    alt={logoMedia.altText || 'Logo'}
                    className="h-8 w-auto"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload new logo
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      successMessage: 'Navbar logo has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Call to action"
            description="The primary button shown on the right side of the navbar."
          >
            {navbarGroups?.ctaFields.some((field) => field.key === 'ctaUrl') ? (
              <p className="mb-4 truncate text-[11px] text-[#a8a098]">
                {fieldValues[
                  navbarGroups.ctaFields.find((field) => field.key === 'ctaUrl')?.id
                ] || ''}
              </p>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              {navbarGroups?.ctaFields
                .filter((field) => field.key !== 'ctaUrl')
                .map((field) => (
                  <div key={field.id}>
                    <label
                      htmlFor={`field-${field.id}`}
                      className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                    >
                      {field.label}
                    </label>
                    <FieldInputRenderer
                      field={field}
                      value={fieldValues[field.id] || ''}
                      onChange={(value) => updateFieldValue(field.id, value)}
                    />
                  </div>
                ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Navigation links"
            description="Each card represents one top-level navbar item. Mega menu links open their dropdown on hover."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {navbarGroups?.linkGroups.map((group) => (
                <LinkGroupEditor
                  key={group.index}
                  index={group.index}
                  fields={group.fields}
                  fieldValues={fieldValues}
                  onFieldChange={updateFieldValue}
                />
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isFooter && (
        <>
          <SectionBlock
            title="Footer logo"
            description="The logo shown in the top-left of the footer brand area."
          >
            <div className="flex flex-wrap items-center gap-4">
              {logoMedia ? (
                <div className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-5 py-4">
                  <img
                    src={resolveMediaUrl(logoMedia.url)}
                    alt={logoMedia.altText || 'Footer logo'}
                    className="h-8 w-auto"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload footer logo
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      altText: 'VBS Invest',
                      successMessage: 'Footer logo has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>

          <SectionBlock
            title="Brand text"
            description="About text, disclaimer, and copyright shown in the footer."
          >
            <div className="grid gap-4">
              {fields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </SectionBlock>

          <SectionBlock
            title="Social links"
            description="Each card is one social icon with its name and external URL. Add or remove links as needed."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {socialMedia.map((item, index) => (
                <FooterSocialEditor
                  key={item.id}
                  item={item}
                  index={index + 1}
                  values={mediaValues[item.id] || { name: '', href: '' }}
                  isUploading={uploadingItemId === item.id}
                  isDeleting={deletingItemId === item.id}
                  onIconUpload={(event) => handleSocialIconUpload(item, event)}
                  onDelete={() => handleSocialDelete(item)}
                  onChange={(values) =>
                    setMediaValues((current) => ({ ...current, [item.id]: values }))
                  }
                />
              ))}
            </div>

            <label
              className={`mt-4 inline-flex cursor-pointer items-center rounded-md border border-dashed border-[#d8cfc0] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] transition hover:border-[#785E32] hover:bg-white ${
                isAddingSocial ? 'pointer-events-none opacity-60' : ''
              }`}
            >
              {isAddingSocial ? 'Adding social link…' : '+ Add social link'}
              <input
                type="file"
                accept="image/*,.svg,.ico"
                className="hidden"
                disabled={isAddingSocial}
                onChange={handleAddSocial}
              />
            </label>
          </SectionBlock>

          <SectionBlock
            title="Bottom logomark"
            description="Small mark shown next to the copyright line."
          >
            <div className="flex flex-wrap items-center gap-4">
              {logomarkMedia ? (
                <div className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-5 py-4">
                  <img
                    src={resolveMediaUrl(logomarkMedia.url)}
                    alt={logomarkMedia.altText || 'Logomark'}
                    className="h-4 w-auto"
                  />
                </div>
              ) : null}
              <label className="inline-flex cursor-pointer items-center rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2.5 text-sm font-medium text-[#130705] hover:bg-white">
                Upload logomark
                <input
                  type="file"
                  accept="image/*,.svg"
                  className="hidden"
                  onChange={(event) =>
                    handleLogoUpload(event, {
                      groupKey: 'logomark',
                      isPrimary: false,
                      altText: 'VBS logomark',
                      successMessage: 'Footer logomark has been updated successfully.',
                    })
                  }
                  disabled={isSaving}
                />
              </label>
            </div>
          </SectionBlock>
        </>
      )}

      {isFooterColumn && (
        <>
          <SectionBlock
            title="Column title"
            description="The heading shown above this group of footer links."
          >
            {footerColumnGroups?.titleField ? (
              <div>
                <label
                  htmlFor={`field-${footerColumnGroups.titleField.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {footerColumnGroups.titleField.label}
                </label>
                <FieldInputRenderer
                  field={footerColumnGroups.titleField}
                  value={fieldValues[footerColumnGroups.titleField.id] || ''}
                  onChange={(value) =>
                    updateFieldValue(footerColumnGroups.titleField.id, value)
                  }
                />
              </div>
            ) : null}
          </SectionBlock>

          <SectionBlock
            title="Column links"
            description="Each card is one footer link. Labels can be edited; routes are fixed."
          >
            <div className="grid gap-4 xl:grid-cols-2">
              {footerColumnGroups?.linkGroups.map((group) => (
                <LinkGroupEditor
                  key={group.index}
                  index={group.index}
                  fields={group.fields}
                  fieldValues={fieldValues}
                  onFieldChange={updateFieldValue}
                />
              ))}
            </div>
          </SectionBlock>
        </>
      )}

      {isMegaMenu && headerFields.length > 0 && (
        <SectionBlock
          title="Mega menu header"
          description="The heading and intro text shown on the left side of the dropdown."
        >
          {megaMenuUrlField && fieldValues[megaMenuUrlField.id] ? (
            <p className="mb-4 truncate text-[11px] text-[#a8a098]">
              {fieldValues[megaMenuUrlField.id]}
            </p>
          ) : null}
          <div className="grid gap-4">
            {headerFields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
          </div>
        </SectionBlock>
      )}

      {!isNavbar && !isMegaMenu && !isFooter && !isFooterColumn && !isHomeHero && !isSolutionsHero && !isWealthPageHero && !isWealthApproach && !isWealthProducts && !isWealthSecurity && !isInfrastructureHero && !isInfrastructureProtocols && !isInfrastructureOperational && !isInfrastructureGlobal && !isInfrastructureNeverStatic && !isPlatformsHero && !isPlatformsDid && !isPlatformsEngineered && !isPlatformsMetaTrader && !isPlatformsEntryPoints && !isVpsHostingHero && !isVpsHostingDedicated && !isVpsHostingWhyClients && !isVpsHostingPackages && !isVpsHostingIncluded && !isVpsHostingRequestToResponse && !isVpsHostingAccessControl && !isVpsHostingFaq && !isInsightsHero && !isInsightsPillars && !isInsightsFeatured && !isInsightsNews && !isInsightsSubscribe && !isEconomicCalendarHero && !isEconomicCalendarEventImpact && !isEconomicCalendarMacroHighlights && !isContactHero && !isContactForm && !isContactSpeak && !isMarketAnalysisHero && !isMarketAnalysisPostsSettings && !isMarketAnalysisMarketInsights && !isMarketAnalysisPostMeta && !isMarketAnalysisPostContent && !isWealthDetailHero && !isWealthDetailEarn && !isWealthDetailCoreParameters && !isWealthDetailSavings && !isWealthDetailAccountFeatures && !isSolutionDetailHero && !isMarketDetailHero && !isSolutionDetailView && !isSolutionDetailWhatWeDeliver && !isSolutionDetailWhyChoose && !isMarketDetailGlobalCompanies && !isMarketDetailMarketSegments && !isMarketDetailFeaturedCompanies && !isMarketDetailMarketAccess && !isMarketDetailRisk && !isTtg && !isWhatWeDo && !isGlobalMarkets && !isWealthSavings && !isHomeMarkets && !isSolutionsOurExpertise && !isTailoredStrategies && !isApproachSteps && !isWhoWeWorkWith && !isAdvantage && !isCtaSection && fields.length > 0 && (
        <SectionBlock title="Fields">
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map((field) => (
              <div
                key={field.id}
                className={field.fieldType === 'textarea' ? 'sm:col-span-2' : ''}
              >
                <label
                  htmlFor={`field-${field.id}`}
                  className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                >
                  {field.label}
                </label>
                <FieldInputRenderer
                  field={field}
                  value={fieldValues[field.id] || ''}
                  onChange={(value) => updateFieldValue(field.id, value)}
                />
              </div>
            ))}
          </div>
        </SectionBlock>
      )}

      {isMegaMenu && itemMedia.length > 0 && (
        <SectionBlock
          title="Dropdown items"
          description="Each card is one link inside the mega menu grid. You can change the icon, title, and subtitle."
        >
          <div className="grid gap-4 xl:grid-cols-2">
            {itemMedia.map((item, index) => (
              <MediaItemEditor
                key={item.id}
                item={item}
                index={index + 1}
                values={mediaValues[item.id] || { title: '', subtitle: '', url: '' }}
                isUploading={uploadingItemId === item.id}
                onIconUpload={(event) => handleItemIconUpload(item, event)}
                onChange={(values) =>
                  setMediaValues((current) => ({ ...current, [item.id]: values }))
                }
              />
            ))}
          </div>
        </SectionBlock>
      )}

      <div className="sticky bottom-0 z-10 -mx-5 border-t border-[#e4d9c8] bg-white/95 px-5 py-4 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="space-y-3">
          <StatusMessage type="success" message={message} onDismiss={() => setMessage('')} />
          <StatusMessage type="error" message={error} onDismiss={() => setError('')} />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[13px] text-[#8a8178]">{saveHint}</p>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="rounded-md !bg-[#785E32] px-5 py-2.5 text-sm font-medium !text-white hover:!bg-[#6a522c] disabled:opacity-60"
            >
              {isSaving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionEditor;
